import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Scale, ShieldCheck, Mail, Cookie, AlertTriangle, FileText, FileDown, Globe, Cpu } from 'lucide-react';
import useLang from '../../hooks/useLang';

/**
 * Reusable legal-content blocks rendered identically in:
 *  - Dedicated pages (/aviso-legal, /politica-privacidad, /politica-cookies)
 *  - Footer-triggered modals (without leaving the home page)
 */


const TransparencyHeader = () => {
  const lang = useLang();

  const changeLanguage = (newLang) => {
    window.dispatchEvent(new CustomEvent('languageChange', { detail: newLang }));
    localStorage.setItem('lang', newLang);
  };

  return (
    <div className="flex justify-start mb-6">
      {/* Recuadro / Pastilla de Transparencia en naranja con letras y punto en verde */}
      <div className="inline-flex items-center gap-3 px-3.5 py-1.5 rounded-full bg-slate-950/60 border border-orange-500/80 shadow-[0_4px_16px_rgba(249,115,22,0.15)] backdrop-blur-md">
        
        {/* Selector de Banderas SVG del menu superior con iluminacion neon al activar */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => changeLanguage('es')}
            title="Español"
            className={`transition-all duration-300 hover:scale-125 focus:outline-none flex items-center shrink-0 ${
              lang === 'es' 
                ? 'filter-none opacity-100 scale-110 drop-shadow-[0_0_5px_rgba(234,179,8,0.6)]' 
                : 'filter grayscale opacity-40 hover:opacity-80'
            }`}
          >
            <img 
              src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/es.svg" 
              alt="Español" 
              className="w-4 h-3 object-cover rounded-sm shadow-sm pointer-events-none" 
            />
          </button>
          <button
            type="button"
            onClick={() => changeLanguage('en')}
            title="English"
            className={`transition-all duration-300 hover:scale-125 focus:outline-none flex items-center shrink-0 ${
              lang === 'en' 
                ? 'filter-none opacity-100 scale-110 drop-shadow-[0_0_5px_rgba(59,130,246,0.6)]' 
                : 'filter grayscale opacity-40 hover:opacity-80'
            }`}
          >
            <img 
              src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/gb.svg" 
              alt="English" 
              className="w-4 h-3 object-cover rounded-sm shadow-sm pointer-events-none" 
            />
          </button>
        </div>

        {/* Separador vertical fino */}
        <div className="w-[1px] h-3 bg-orange-500/30" />

        {/* Indicador con Punto Parpadeante y letras en VERDE */}
        <div className="flex items-center gap-2">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
          </span>
          <span className="text-[9px] font-bold text-emerald-400 tracking-wider uppercase font-mono animate-pulse">
            {lang === 'es' ? 'Actualización Normativa Activa' : 'Regulatory Sync Active'}
          </span>
        </div>

      </div>
    </div>
  );
};

export const AvisoLegalContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Scale className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Información Legal' : 'Legal Information'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Aviso Legal y Condiciones de Uso' : 'Legal Notice & Terms of Use'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento con el deber de información recogido en el artículo 10 de la Ley
                34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y del
                Comercio Electrónico (LSSI-CE), se detallan a continuación los datos de información
                general del sitio web:
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1.1. Datos Identificativos</h2>
                <ul className="space-y-2 list-disc list-inside text-foreground">
                  <li><strong>Titular:</strong> Joaquín Burgos Bayo (en adelante, "Auditorialegal")</li>
                  <li><strong>NIF:</strong> 44224426A</li>
                  <li><strong>Domicilio Profesional:</strong> Puerto del Carmen, Edificio Atalaya Nº 17, C.P. 35510, Lanzarote.</li>
                  <li><strong>Correo Electrónico:</strong> info@auditorialegal.es</li>
                  <li><strong>Teléfono:</strong> 633115067</li>
                  <li><strong>Actividad:</strong> Consultoría de ciberseguridad, auditoría forense (Esquema Nacional de Seguridad) y peritaje tecnológico.</li>
                  <li><strong>Acreditaciones:</strong> Auditorías internas certificadas en base al Centro Criptológico Nacional (CCN).</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1.2. Condiciones de Uso, Propiedad Intelectual y Defensas Técnicas</h2>
                <p className="leading-relaxed mb-4">
                  El acceso y/o uso de este portal web atribuye la condición de USUARIO, que acepta,
                  desde dicho acceso, las presentes Condiciones Generales de Uso.
                </p>
                <p className="leading-relaxed mb-4">
                  El contenido de la web (textos, informes genéricos, metodologías publicadas como
                  <em> Full-Stack Compliance UE 2026</em>, imágenes y diseño) is propiedad exclusiva
                  de Auditorialegal. Queda expresamente prohibida la reproducción, distribución o
                  comunicación pública de la totalidad o parte de los contenidos de esta web con
                  fines comerciales sin la autorización expresa de su titular.
                </p>
                <p className="leading-relaxed mb-4">
                  La información contenida en el sitio web tiene carácter meramente informativo y
                  divulgativo. En ningún caso la descarga de materiales, tests de exposición o la
                  lectura de los contenidos sustituye a un dictamen pericial o asesoramiento
                  jurídico-técnico personalizado.
                </p>
                <p className="leading-relaxed">
                  Asimismo, queda terminantemente prohibida la técnica del "framing" o incrustación de este sitio web o de cualquiera de sus elementos en portales de terceros sin autorización previa por escrito. Auditorialegal implementa encabezados HTTP y defensas técnicas anti-clickjacking robustas en cliente y servidor para mitigar ataques que pretendan alterar o suplantar la interfaz de la plataforma corporativa.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Nota Importante:</strong> Este aviso legal rige la
                  relación entre el usuario y Auditorialegal en cuanto al uso del sitio web. Para
                  servicios de auditoría, consultoría o peritaje, se establecerán condiciones
                  específicas mediante contratos de consultorías profesionales.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 24 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with the information duties established in Article 10 of Law
                34/2002, of July 11, on Information Society Services and Electronic Commerce
                (LSSI-CE), the general information details of this website are listed below:
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1.1. Identification Data</h2>
                <ul className="space-y-2 list-disc list-inside text-foreground">
                  <li><strong>Owner:</strong> Joaquín Burgos Bayo (hereinafter, "Auditorialegal")</li>
                  <li><strong>Tax ID (NIF):</strong> 44224426A</li>
                  <li><strong>Professional Address:</strong> Puerto del Carmen, Edificio Atalaya No. 17, Zip Code 35510, Lanzarote, Spain.</li>
                  <li><strong>Email:</strong> info@auditorialegal.es</li>
                  <li><strong>Phone:</strong> +34 633115067</li>
                  <li><strong>Activity:</strong> Cybersecurity consulting, digital forensics, National Security Scheme (ENS) audits, and tech expert witness testimonies.</li>
                  <li><strong>Accreditations:</strong> Certified internal audits based on the Spanish National Cryptologic Center (CCN) guidelines.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1.2. Terms of Use, Intellectual Property, and Technical Safeguards</h2>
                <p className="leading-relaxed mb-4">
                  Access and/or use of this web portal attributes the condition of USER, who accepts
                  these General Terms of Use from the moment of such access.
                </p>
                <p className="leading-relaxed mb-4">
                  The content of this website (texts, generic reports, methodologies published like
                  <em> Full-Stack Compliance UE 2026</em>, images, and design) is the exclusive property
                  of Auditorialegal. Any reproduction, distribution, or public communication of all or part
                  of the contents of this website for commercial purposes without explicit permission is strictly prohibited.
                </p>
                <p className="leading-relaxed mb-4">
                  The information on this website is for general educational and informational purposes. Downloading materials,
                  running exposure tests, or reading the content does not replace a customized technological expert opinion or professional legal-tech advice.
                </p>
                <p className="leading-relaxed">
                  Likewise, the "framing" or embedding of this website or any of its elements on third-party portals without prior written authorization is strictly prohibited. Auditorialegal implements HTTP headers and robust anti-clickjacking technical defenses on both client and server sides to mitigate attacks aimed at altering or spoofing the corporate platform interface.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Important Note:</strong> This legal notice governs the
                  relationship between the user and Auditorialegal regarding the use of this website. For
                  auditing, consulting, or forensics services, specific conditions will be established through professional contracts.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 24, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PoliticaPrivacidadContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Protección de Datos' : 'Data Protection'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Cumplimiento RGPD y LOPDGDD' : 'GDPR & LOPDGDD Compliance'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2 mb-8'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En Auditorialegal aplicamos el principio de <strong>"Privacidad por Diseño"</strong>.
                Como firma dedicada a la auditoría de cumplimiento normativo (RGPD y ENS), garantizo
                que el tratamiento de sus datos personales se realiza con el máximo rigor y
                transparencia, en cumplimiento con el Reglamento (UE) 2016/679 (RGPD) y la Ley
                Orgánica 3/2018 (LOPDGDD).
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.1. ¿Quién es el Responsable del Tratamiento?</h2>
                <ul className="space-y-2 list-disc list-inside text-foreground">
                  <li><strong>Identidad:</strong> Joaquín Burgos Bayo</li>
                  <li><strong>Email de contacto para privacidad:</strong> info@auditorialegal.es</li>
                  <li><strong>Delegado de Protección de Datos (DPO):</strong> Conforme al Art. 37 del RGPD y al Art. 34 de la LOPDGDD, Auditorialegal no está obligada a designar un DPO, asumiendo el propio titular directamente la máxima responsabilidad sobre la privacidad y el ejercicio de derechos.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.2. ¿Con qué finalidad y base legal tratamos sus datos?</h2>
                <p className="leading-relaxed mb-4">
                  Trato la información que me facilita a través del formulario de contacto exclusivamente para:
                </p>
                <ol className="list-decimal list-inside space-y-4">
                  <li>
                    <strong>Gestionar la solicitud de "Diagnóstico Express":</strong> Incluyendo la realización del pre-análisis de exposición externa (OSINT pasivo) sobre el dominio web proporcionado.
                    <ul className="ml-6 mt-2 list-disc list-inside text-sm">
                      <li><strong>Base Legal:</strong> Aplicación de medidas precontractuales a petición del interesado (Art. 6.1.b RGPD).</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Contacto profesional:</strong> Responder a consultas o solicitudes de presupuestos.
                    <ul className="ml-6 mt-2 list-disc list-inside text-sm">
                      <li><strong>Base Legal:</strong> Interés legítimo corporativo (Art. 6.1.f RGPD) y consentimiento explícito (Art. 6.1.a RGPD).</li>
                    </ul>
                  </li>
                </ol>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Importante:</strong> Al solicitar un diagnóstico, usted autoriza expresamente la recolección de información pública (OSINT) de su dominio e infraestructura externa, garantizando que Auditorialegal no realizará técnicas intrusivas (DAST activo) sin autorización firmada.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.3. ¿Cuánto tiempo conservaremos sus datos?</h2>
                <p className="leading-relaxed">
                  Los datos personales proporcionados se conservarán durante el tiempo necesario para resolver su consulta. Si la consulta deriva en un encargo profesional o informe pericial, los datos se conservarán durante un máximo de <strong>5 años</strong> tras la finalización del servicio, para atender a posibles responsabilidades legales derivadas del peritaje (Art. 335 LEC). Una vez cumplido este plazo, los datos serán bloqueados y destruidos mediante métodos seguros.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.4. ¿A qué destinatarios se comunicarán sus datos y transferencias internacionales?</h2>
                <p className="leading-relaxed mb-4">
                  Auditorialegal <strong>no cede, vende ni transfiere</strong> sus datos a terceros comerciales, salvo obligación legal. Sin embargo, para la correcta operatividad técnica de la plataforma web y la prestación del servicio se utilizan los siguientes proveedores tecnológicos auxiliares que actúan bajo estrictas directivas de privacidad:
                </p>
                <ul className="space-y-3 list-disc list-inside text-foreground text-sm mb-4">
                  <li>
                    <strong>Infraestructura de Alojamiento y Servidores:</strong> Proveedores con centros de datos y almacenamiento ubicados íntegramente dentro del Espacio Económico Europeo (EEE), garantizando que no existen transferencias de datos de bases de datos persistentes fuera de la Unión Europea.
                  </li>
                  <li>
                    <strong>jsDelivr CDN (Prospectone Sp. z o.o.):</strong> Utilizado para la carga rápida y distribución segura de scripts de rendimiento y accesibilidad con soporte de Integridad de Subrecursos (SRI). La carga de recursos técnicos transmite temporalmente metadatos técnicos (dirección IP de conexión). Prospectone es un proveedor europeo con sede en Polonia (Polonia, UE).
                  </li>
                  <li>
                    <strong>Google Fonts (Google LLC / Google Ireland Limited):</strong> Las tipografías de diseño moderno se cargan directamente de forma optimizada. Este servicio puede capturar metadatos técnicos (IP del dispositivo y User-Agent) para la entrega del recurso tipográfico. Al ser una entidad de EE.UU., estas transferencias internacionales se legitiman mediante el **Data Privacy Framework (DPF) UE-EE.UU.** o, en su defecto, bajo las Cláusulas Contractuales Tipo (CCT) de la Comisión Europea (Art. 13.1.f y 46 del RGPD).
                  </li>
                  <li>
                    <strong>Google Gemini API (Google Ireland Limited):</strong> Las consultas del asistente de IA se procesan de forma transitoria por Google. Legitimación mediante CCT aprobadas por la Comisión Europea (ver sección 2.5).
                  </li>
                  <li>
                    <strong>PostHog Analytics (PostHog Inc. / PostHog Europe):</strong> Herramienta de analítica pseudoanonimizada que solo se activa previo consentimiento expreso en el banner de cookies.
                  </li>
                  <li>
                    <strong>Dataprius (Dataprius S.L.):</strong> Proveedor de almacenamiento en la nube privada y cifrada con servidores ubicados en España (UE). Se utiliza de forma exclusiva para el almacenamiento y la entrega 100% segura y confidencial de los informes periciales, análisis OSINT y diagnósticos de cumplimiento de los clientes, garantizando inmunidad a accesos externos y el pleno cumplimiento del RGPD.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.5. Asistente Virtual (Inteligencia Artificial · Google Gemini)</h2>
                <p className="leading-relaxed mb-3">
                  Este sitio web incorpora un <strong>asistente virtual basado en IA</strong> impulsado
                  por el modelo <strong>Gemini de Google</strong> (Google LLC / Google Ireland Limited).
                  Cuando usted utiliza el chat, las consultas escritas se transmiten en tiempo real a la
                  API de Gemini exclusivamente para generar la respuesta. Auditorialegal solo almacena
                  internamente el historial conversacional asociado a su sesión (en servidores propios
                  ubicados en el EEE) para mantener el contexto durante la conversación.
                </p>
                <ul className="space-y-2 list-disc list-inside text-foreground text-sm">
                  <li>
                    <strong>Base legal:</strong> Consentimiento expreso del usuario al iniciar la
                    conversación (Art. 6.1.a RGPD).
                  </li>
                  <li>
                    <strong>Datos tratados:</strong> Únicamente el contenido textual del mensaje. No se
                    recogen identificadores personales salvo que usted los introduzca voluntariamente.
                  </li>
                  <li>
                    <strong>Transferencia internacional:</strong> Google puede procesar los mensajes
                    fuera del EEE conforme a sus cláusulas contractuales tipo (SCC) aprobadas por la
                    Comisión Europea. Más información en{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      policies.google.com/privacy
                    </a>.
                  </li>
                  <li>
                    <strong>Conservación:</strong> El historial conversacional se elimina automáticamente
                    tras 30 días o cuando el usuario cierra el navegador.
                  </li>
                  <li>
                    <strong>Recomendación:</strong> No incluya en el chat datos confidenciales,
                    identificadores fiscales, contraseñas ni información sensible. Para análisis
                    específicos, utilice el formulario o el email institucional.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.6. ¿Cuáles son sus derechos y cómo ejercerlos?</h2>
                <p className="leading-relaxed mb-4">
                  Como interesado, el RGPD y la LOPDGDD le garantizan el control absoluto sobre sus datos personales. Puede ejercer de forma totalmente gratuita cualquiera de los siguientes derechos, acreditando su identidad:
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 mb-6">
                  <a 
                    href="/pdfs/formulario-derecho-de-acceso.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">1. Derecho de Acceso</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-rectificacion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">2. Derecho de Rectificación</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-oposicion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">3. Derecho de Oposición</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-supresion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">4. Derecho de Supresión ("Al Olvido")</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-limitacion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">5. Derecho de Limitación del Tratamiento</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-portabilidad.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">6. Derecho de Portabilidad</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-oposicion-decisiones-automatizadas.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group sm:col-span-2"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">7. Derecho a no ser objeto de Decisiones Automatizadas</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                </div>

                <p className="leading-relaxed">
                  Para ejercer cualquiera de estos derechos, puede descargar el formulario correspondiente en PDF, rellenarlo y enviarlo escaneado junto con una copia de su DNI o documento equivalente a la dirección de correo electrónico <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-mono">info@auditorialegal.es</a>. También tiene derecho a presentar una reclamación directa ante la Agencia Española de Protección de Datos (AEPD) a través de su sede electrónica (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.aepd.es</a>).
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.7. Seguridad de la Información y Gestión de Brechas</h2>
                <p className="leading-relaxed mb-3">
                  En Auditorialegal implementamos rigurosas medidas de seguridad técnicas y organizativas para asegurar la confidencialidad, integridad y disponibilidad permanentes de sus datos. 
                </p>
                <p className="leading-relaxed">
                  Ante cualquier posible brecha de seguridad que afecte a los datos personales bajo nuestro control, contamos con un protocolo pericial inmediato de detección y contención. En caso de detectarse un riesgo real para los derechos de los usuarios, nos comprometemos a notificarlo a la Agencia Española de Protección de Datos (AEPD) en un plazo máximo de <strong>72 horas</strong>, y a los propios interesados con total transparencia para que puedan tomar las medidas correspondientes.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.8. Canal de Empleo y Envío de Currículums</h2>
                <p className="leading-relaxed mb-3">
                  Si nos remite su currículum vitae a través de nuestros canales de contacto, sus datos se tratarán con la única finalidad de hacerle partícipe de posibles procesos de colaboración o selección profesional en la firma.
                </p>
                <p className="leading-relaxed">
                  La base de legitimación del tratamiento es el consentimiento expreso otorgado por usted al remitir de forma voluntaria sus datos (Art. 6.1.a RGPD). Garantizamos que la información de su currículum se conservará de forma totalmente confidencial durante un plazo máximo de <strong>1 año</strong>, procediéndose a su destrucción y borrado físico seguro una vez transcurrido dicho periodo si no se ha iniciado una relación profesional.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.9. Límite de Edad y Consentimiento para el Asistente Virtual</h2>
                <p className="leading-relaxed mb-3">
                  De conformidad con el artículo 7 de la Ley Orgánica 3/2018 (LOPDGDD), el tratamiento de datos personales de menores solo podrá fundarse en su consentimiento cuando sea mayor de catorce años. 
                </p>
                <p className="leading-relaxed">
                  Este sitio web, sus herramientas y el Asistente Virtual interactivo con Inteligencia Artificial (impulsado por Google Gemini) están dirigidos de manera exclusiva a mayores de 14 años. Queda estrictamente prohibido el uso del chat automatizado y el envío de consultas por parte de menores de dicha edad sin la debida supervisión y consentimiento explícito de sus padres o tutores legales.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 24 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                At Auditorialegal we apply the <strong>"Privacy by Design"</strong> principle.
                As a firm dedicated to regulatory compliance audits (GDPR and ENS), I guarantee that
                the processing of your personal data is performed with the highest rigor and transparency,
                in compliance with Regulation (EU) 2016/679 (GDPR) and Spanish Organic Law 3/2018 (LOPDGDD).
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.1. Who is the Data Controller?</h2>
                <ul className="space-y-2 list-disc list-inside text-foreground">
                  <li><strong>Identity:</strong> Joaquín Burgos Bayo</li>
                  <li><strong>Contact Email:</strong> info@auditorialegal.es</li>
                  <li><strong>Data Protection Officer (DPO):</strong> In accordance with Art. 37 of the GDPR and Art. 34 of the LOPDGDD, Auditorialegal is not required to appoint a DPO. The owner directly assumes full responsibility for privacy and the exercise of user rights.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.2. Purposes and Legal Basis for Processing</h2>
                <p className="leading-relaxed mb-4">
                  We process the information you provide through our contact form exclusively to:
                </p>
                <ol className="list-decimal list-inside space-y-4">
                  <li>
                    <strong>Manage the "Express Diagnosis" request:</strong> This includes a passive external exposure assessment (passive OSINT) on the domain name provided.
                    <ul className="ml-6 mt-2 list-disc list-inside text-sm">
                      <li><strong>Legal Basis:</strong> Implementation of pre-contractual measures at the request of the data subject (Art. 6.1.b GDPR).</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Professional Contact:</strong> Respond to inquiries, assistance, or quote requests.
                    <ul className="ml-6 mt-2 list-disc list-inside text-sm">
                      <li><strong>Legal Basis:</strong> Corporate legitimate interest (Art. 6.1.f GDPR) and explicit consent (Art. 6.1.a GDPR).</li>
                    </ul>
                  </li>
                </ol>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Important Notice:</strong> By requesting an express diagnosis, you explicitly authorize the collection of public data (OSINT) regarding your infrastructure and domain. Auditorialegal will never perform active or intrusive vulnerability scans (active DAST) without a formally signed service agreement.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.3. How long will we retain your data?</h2>
                <p className="leading-relaxed">
                  Personal data provided will be kept for as long as necessary to resolve your request. If the inquiry leads to a professional contract or forensic peritaje, data will be kept for a maximum of <strong>5 years</strong> after the service ends to address any potential legal liabilities in court (Art. 335 of the Spanish Civil Procedure Act). Once this period expires, the data will be securely blocked and permanently destroyed.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.4. Data Recipients & International Transfers</h2>
                <p className="leading-relaxed mb-4">
                  Auditorialegal <strong>does not share, sell, or rent</strong> your personal data to any commercial third parties. However, for the technical operation of the website and services, we utilize auxiliary tech providers operating under strict privacy mandates:
                </p>
                <ul className="space-y-3 list-disc list-inside text-foreground text-sm mb-4">
                  <li>
                    <strong>Hosting Infrastructure & Cloud Servers:</strong> Providers with data centers and persistent storage located entirely within the European Economic Area (EEA), ensuring no persistent database transfers outside the EU.
                  </li>
                  <li>
                    <strong>jsDelivr CDN (Prospectone Sp. z o.o.):</strong> For secure, rapid asset delivery using Subresource Integrity (SRI). Cdn resource loads temporarily transmit technical metadata (connection IP address). Prospectone is a European provider based in Poland.
                  </li>
                  <li>
                    <strong>Google Fonts (Google Ireland Limited):</strong> Modern typography assets loaded from secure CDN. This service may capture IP address and browser User-Agent details. International data transfers are certified under the **EU-U.S. Data Privacy Framework (DPF)** or EU Standard Contractual Clauses (SCC) (Art. 13.1.f and 46 GDPR).
                  </li>
                  <li>
                    <strong>Google Gemini API (Google Ireland Limited):</strong> Legal Assistant AI chat messages are processed in real-time by Google. This data is protected under Standard Contractual Clauses (SCC) approved by the European Commission.
                  </li>
                  <li>
                    <strong>PostHog Analytics (PostHog Inc.):</strong> Pseudo-anonymous product analytics tool, only activated upon explicit opt-in in the cookie banner.
                  </li>
                  <li>
                    <strong>Dataprius (Dataprius S.L.):</strong> Provider of secure, encrypted private cloud storage with servers located entirely in Spain (EU). Used exclusively for storing and delivering preliminary assessments and exposure reports securely, guaranteeing strict GDPR compliance.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.5. Virtual AI Assistant (Google Gemini)</h2>
                <p className="leading-relaxed mb-3">
                  This website incorporates an <strong>AI-powered virtual assistant</strong> driven by 
                  Google\'s <strong>Gemini model</strong>. When you use the chat widget, your text inquiries are 
                  transmitted in real-time to the Gemini API to generate responses. Auditorialegal only stores 
                  conversational histories transitorily in secure servers located within the EEA to maintain context.
                </p>
                <ul className="space-y-2 list-disc list-inside text-foreground text-sm">
                  <li>
                    <strong>Legal Basis:</strong> Explicit consent of the user when starting the conversation (Art. 6.1.a GDPR).
                  </li>
                  <li>
                    <strong>Data Processed:</strong> Only the text content of your messages. No personal identifiers are collected unless voluntarily provided.
                  </li>
                  <li>
                    <strong>International Transfers:</strong> Google may process messages outside the EEA under the Standard Contractual Clauses (SCC) approved by the European Commission. Read more at{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      policies.google.com/privacy
                    </a>.
                  </li>
                  <li>
                    <strong>Retention:</strong> Conversations are automatically deleted after 30 days or once you close your browser.
                  </li>
                  <li>
                    <strong>Recommendation:</strong> Please do not share confidential details, passwords, bank accounts, or sensitive private data in the chat.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.6. User Rights & Exercise (ARSOPOL)</h2>
                <p className="leading-relaxed mb-4">
                  The GDPR guarantees you absolute control over your personal data. You can exercise any of the following rights free of charge by proving your identity. Click to download the Spanish/EU formal templates:
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 mb-6">
                  <a 
                    href="/pdfs/formulario-derecho-de-acceso.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">1. Right of Access</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-rectificacion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">2. Right of Rectification</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-oposicion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">3. Right of Objection</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-supresion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">4. Right to Erasure ("To Be Forgotten")</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-limitacion.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">5. Right to Restriction of Processing</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-portabilidad.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">6. Right to Data Portability</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                  <a 
                    href="/pdfs/formulario-derecho-de-oposicion-decisiones-automatizadas.pdf" 
                    download
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all text-xs font-medium group sm:col-span-2"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      <span className="text-slate-200">7. Right not to be Subject to Automated Decision-Making</span>
                    </div>
                    <FileDown className="w-4 h-4 text-slate-500 group-hover:text-accent transition-colors" />
                  </a>
                </div>

                <p className="leading-relaxed">
                  To exercise any of these rights, please download the corresponding template, fill it out, and send it scanned along with a copy of your ID or equivalent document to <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-mono">info@auditorialegal.es</a>. You also have the right to lodge a formal complaint with the Spanish Data Protection Agency (AEPD) at <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.aepd.es</a>.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.7. Information Security & Data Breach Protocol</h2>
                <p className="leading-relaxed mb-3">
                  At Auditorialegal we implement strict technical and organizational security measures to secure the confidentiality, integrity, and ongoing availability of your personal data.
                </p>
                <p className="leading-relaxed">
                  In case of any potential data breach that affects the personal data under our control, we maintain an immediate forensic detection and containment protocol. If a real risk to user rights is identified, we commit to notify the Spanish Data Protection Agency (AEPD) within a maximum of <strong>72 hours</strong>, and notify the affected individuals with absolute transparency.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.8. Job Recruitment & CV Submissions</h2>
                <p className="leading-relaxed mb-3">
                  If you send us your curriculum vitae through our contact channels, your data will be processed solely to include you in potential selection and professional collaboration processes.
                </p>
                <p className="leading-relaxed">
                  The processing is legitimized by the explicit consent granted when submitting your data (Art. 6.1.a GDPR). We guarantee that CV information is kept completely confidential for a maximum of <strong>1 year</strong>, after which it will be safely and permanently destroyed.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2.9. Age Limit & Virtual Assistant AI Consent</h2>
                <p className="leading-relaxed mb-3">
                  In accordance with Article 7 of Spanish Organic Law 3/2018 (LOPDGDD), data processing for minors is only valid under their own consent if they are over 14 years old.
                </p>
                <p className="leading-relaxed">
                  This website, its technical tools, and the interactive Virtual AI Assistant (driven by Google Gemini) are strictly directed to individuals aged 14 and above. Use of the automated chat and submission of queries by minors under this age is strictly prohibited without the express supervision and consent of their parents or legal guardians.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 24, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {!inModal && (
        <Card className="border-accent/30 bg-accent-subtle">
          <CardContent className="pt-6 text-center">
            <Mail className="w-10 h-10 text-accent mx-auto mb-3" />
            <p className="text-sm text-foreground leading-relaxed">
              {lang === 'es' 
                ? <>Para cualquier duda sobre esta política de privacidad o para ejercer sus derechos, contácteme en <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold">info@auditorialegal.es</a></>
                : <>For any questions regarding this privacy policy or to exercise your rights, contact me at <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold">info@auditorialegal.es</a></>}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const PoliticaCookiesContent = ({ inModal = false, onOpenPreferences }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Cookie className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Tecnologías Web' : 'Web Technologies'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Cookies' : 'Cookie Policy'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Cumplimiento Art. 22 LSSI-CE' : 'ePrivacy & Cookie Compliance'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-2 border-accent/30 bg-accent-subtle mb-4' : 'border-2 border-alert/30 bg-alert-subtle mb-8'}>
        <CardContent className="pt-6 flex items-start gap-4">
          <AlertTriangle className="w-8 h-8 text-accent flex-shrink-0 mt-1" />
          <div>
            <p className="text-sm font-semibold text-primary mb-2">
              {lang === 'es' ? 'Gestión activa de cookies' : 'Active Cookie Management'}
            </p>
            <p className="text-sm text-foreground leading-relaxed">
              {lang === 'es' 
                ? 'En esta web se ha implementado un banner de consentimiento conforme al Art. 22 LSSI-CE que bloquea las cookies opcionales hasta obtener su autorización.'
                : 'This website implements a cookie consent banner compliant with ePrivacy regulations (Art. 22 LSSI-CE) which blocks optional scripts until you explicitly consent.'}
              {onOpenPreferences && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={onOpenPreferences}
                    data-testid="open-cookie-preferences-inline"
                    className="text-accent hover:underline font-semibold"
                  >
                    {lang === 'es' ? 'Abrir panel de preferencias →' : 'Open Cookie Settings →'}
                  </button>
                </>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                El dominio www.auditorialegal.es utiliza cookies para garantizar el correcto
                funcionamiento de la web. En cumplimiento con el Art. 22.2 de la LSSI-CE, detallo
                el uso de estas tecnologías.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.1. ¿Qué son las cookies?</h2>
                <p className="leading-relaxed">
                  Las cookies son pequeños ficheros de texto que se descargan en el dispositivo del
                  usuario al acceder a determinadas páginas web.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.2. ¿Qué tipos de cookies utiliza esta web?</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-3">
                      Cookies Técnicas (Estrictamente necesarias)
                    </h3>
                    <p className="leading-relaxed">
                      Aquellas que permiten la navegación a través del sitio web y la utilización de
                      las opciones de seguridad. No requieren consentimiento expreso y no pueden
                      desactivarse.
                    </p>
                  </div>
                  <div className="bg-muted p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-primary mb-3">
                      Cookies de Análisis / Medición
                    </h3>
                    <p className="leading-relaxed">
                      Tratadas por mí o por terceros, me permiten cuantificar el número de usuarios y
                      realizar la medición y análisis estadístico. <strong>Solo se instalarán si usted
                      otorga su consentimiento expreso.</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.3. Configuración y Revocación</h2>
                <p className="leading-relaxed mb-4">
                  Usted puede permitir, bloquear o eliminar las cookies instaladas en su equipo
                  mediante el panel de configuración de la web o mediante la configuración de las
                  opciones de su navegador.
                </p>
                <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                  <p className="text-sm text-foreground leading-relaxed">
                    <strong className="text-primary">Nota:</strong> Si decide desactivar todas las
                    cookies, algunas funcionalidades del sitio web podrían verse limitadas, aunque el
                    acceso a la información principal siempre estará disponible.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.4. Cookies de Terceros y Servicios Externos</h2>
                <p className="leading-relaxed mb-3">
                  Actualmente, Auditorialegal no utiliza cookies de terceros para publicidad ni para
                  analítica avanzada. No obstante, el sitio incorpora dos servicios externos cuya
                  interacción debe conocer:
                </p>
                <ul className="space-y-2 list-disc list-inside text-foreground text-sm">
                  <li>
                    <strong>Asistente IA con Google Gemini:</strong> cuando interactúa con el chat
                    flotante, sus consultas se envían a la API de Google Gemini para generar la
                    respuesta. Este servicio <strong>no instala cookies</strong> en su navegador, pero
                    implica una transferencia internacional de datos cubierta por las cláusulas
                    contractuales tipo aprobadas por la Comisión Europea. Más información en{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      policies.google.com/privacy
                    </a>
                    . Detalles completos en la Política de Privacidad, sección 2.5.
                  </li>
                  <li>
                    <strong>PostHog (analítica de producto):</strong> solo se activa si usted lo autoriza
                    en el banner de cookies. Las cookies <code>ph_*</code> se configuran de forma
                    pseudoanonimizada.
                  </li>
                </ul>
                <p className="leading-relaxed mt-3">
                  En caso de incorporarse nuevos servicios en el futuro, se actualizará esta política y
                  se solicitará el consentimiento correspondiente.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.5. Actualizaciones de la Política</h2>
                <p className="leading-relaxed">
                  Esta Política de Cookies puede modificarse en función de exigencias legislativas,
                  reglamentarias o con la finalidad de adaptar dicha política a las instrucciones
                  dictadas por la Agencia Española de Protección de Datos. Se recomienda revisar
                  esta política periódicamente.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 24 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                The domain www.auditorialegal.es uses cookies to ensure the proper functioning
                of the website. In compliance with ePrivacy guidelines (Art. 22.2 of the LSSI-CE),
                we detail below the use of these tracking technologies.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.1. What are cookies?</h2>
                <p className="leading-relaxed">
                  Cookies are small text files downloaded to your device when visiting certain web pages.
                  They are widely used to make websites work more efficiently and securely.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.2. What types of cookies do we use?</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-primary mb-3">
                      Technical Cookies (Strictly Necessary)
                    </h3>
                    <p className="leading-relaxed">
                      Essential for browsing and operating security features on our site. They do not require
                      prior consent and cannot be disabled under ePrivacy laws.
                    </p>
                  </div>
                  <div className="bg-muted p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-primary mb-3">
                      Analytics / Measurement Cookies
                    </h3>
                    <p className="leading-relaxed">
                      Processed by us or third parties, they allow us to analyze traffic volume and perform
                      statistical metrics. <strong>These are only installed if you grant explicit consent.</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.3. Configuration and Opt-Out</h2>
                <p className="leading-relaxed mb-4">
                  You can permit, block, or delete cookies installed on your device through the cookie banner
                  on the web page or via your browser configuration.
                </p>
                <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                  <p className="text-sm text-foreground leading-relaxed">
                    <strong className="text-primary">Note:</strong> If you disable analytics cookies, some
                    non-essential tracking functionalities will be offline, but full access to website information remains open.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.4. Third-Party Services & APIs</h2>
                <p className="leading-relaxed mb-3">
                  Currently, Auditorialegal does not use third-party cookies for commercial advertising or complex remarketing.
                  However, the site integrates two external APIs whose data transfers you must know:
                </p>
                <ul className="space-y-2 list-disc list-inside text-foreground text-sm">
                  <li>
                    <strong>Virtual AI Assistant (Google Gemini API):</strong> when interacting with our chat,
                    text inquiries are sent in real-time to Google Gemini servers to generate answers. This service 
                    <strong> does not install tracking cookies</strong> in your browser. All data processing is protected by EuropeanSCC. Read more at{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline"
                    >
                      policies.google.com/privacy
                    </a>. Full details are available in Section 2.5 of the Privacy Policy.
                  </li>
                  <li>
                    <strong>PostHog Analytics:</strong> active only if explicitly consented in our cookie banner.
                    Sets pseudo-anonymous cookies (prefixed with <code>ph_*</code>) on your browser.
                  </li>
                </ul>
                <p className="leading-relaxed mt-3">
                  Should we integrate new services, this policy will be promptly updated and consent will be requested.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3.5. Cookie Policy Updates</h2>
                <p className="leading-relaxed">
                  This Cookie Policy may be modified to adapt to new legislation, regulations, or guidelines issued by the
                  Spanish Data Protection Agency (AEPD). We recommend reviewing this policy periodically.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 24, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const TerminosCondicionesContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Scale className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Condiciones Contractuales' : 'Contractual Terms'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Términos y Condiciones de Servicio' : 'Terms and Conditions of Service'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Condiciones generales de contratación y peritaje' : 'General terms of contracting and forensic expertise'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                Las presentes Condiciones Generales de Contratación regulan la prestación de los servicios profesionales de auditoría de ciberseguridad, consultoría técnica y peritaje informático forense ofrecidos por Joaquín Burgos Bayo (en adelante, "Auditorialegal").
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.1. Objeto y Ámbito de Aplicación</h2>
                <p className="leading-relaxed">
                  Estas condiciones se aplican a todas las consultas, diagnósticos express, pre-análisis OSINT y servicios profesionales contratados formalmente a través del sitio web o cualquier otro canal de comunicación habilitado por Auditorialegal.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.2. Solicitudes de Diagnóstico y Límites Técnicos</h2>
                <p className="leading-relaxed mb-4">
                  Al solicitar un "Diagnóstico Express", el usuario consiente de manera informada y expresa la realización de técnicas de recolección de información pasiva y semi-pasiva (OSINT) sobre el dominio o dominios que haya declarado ser de su propiedad o para los cuales disponga de autorización explícita.
                </p>
                <p className="leading-relaxed">
                  Auditorialegal se compromete a no realizar en este diagnóstico preliminar ningún tipo de análisis intrusivo, escaneo de puertos activo con alteración de tráfico o explotación de vulnerabilidades (DAST activo) que pudiese comprometer la disponibilidad o integridad de los sistemas del usuario sin la firma previa de un contrato de consultorías de pentesting (hoja de encargo técnico).
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.3. Secreto Profesional y Confidencialidad</h2>
                <p className="leading-relaxed">
                  En virtud de la naturaleza de los servicios de peritaje informático forense y auditoría judicial, toda la información compartida por el cliente, los hallazgos en auditorías ENS o NIS2, y los informes resultantes se hallan estrictamente protegidos bajo el Secreto Profesional de acuerdo con el artículo 542.3 de la Ley Orgánica del Poder Judicial y las normas éticas del peritaje tecnológico forense.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.4. Limitación de Responsabilidad</h2>
                <p className="leading-relaxed">
                  Los resultados de los informes técnicos preliminares o diagnósticos automatizados se fundamentan en el estado de la infraestructura externa visible en la fecha exacta de su realización. Ciberseguridad es un proceso dinámico; por tanto, Auditorialegal no asume responsabilidad alguna por incidentes, intrusiones o fallos de seguridad sobrevenidos con posterioridad a la emisión del dictamen.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.5. Ley Aplicable, Resolución Extrajudicial y Jurisdicción</h2>
                <p className="leading-relaxed mb-4">
                  En lo no previsto en estas condiciones, será de aplicación la legislación española. Con el objetivo de agilizar y economizar la resolución de disputas, las partes acuerdan intentar resolver cualquier conflicto de buena fe de manera extrajudicial.
                </p>
                <p className="leading-relaxed mb-4">
                  En caso de conflicto planteado por un cliente con condición legal de consumidor final, se someterá de mutuo acuerdo al **Sistema Arbitral de Consumo (Real Decreto 231/2008)**. Para disputas de carácter mercantil B2B entre empresas o profesionales, las partes acuerdan someterse al arbitraje institucional de la **Corte de Arbitraje de la Cámara de Comercio de Las Palmas**.
                </p>
                <p className="leading-relaxed">
                  Si el arbitraje no llegara a celebrarse o fuese declarado nulo por órgano competente, ambas partes se someten, con renuncia expresa a cualquier otro fuero que pudiera corresponderles, a la jurisdicción exclusiva de los Juzgados y Tribunales de Arrecife, Lanzarote (España), salvo en aquellos supuestos procesales donde la ley determine un fuero imperativo de protección al consumidor.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.6. Compromiso de Trato y Decoro</h2>
                <p className="leading-relaxed">
                  Las comunicaciones y el trato profesional entre Auditorialegal y el cliente se regirán bajo los principios de buena fe y deberán conservar en todo momento el decoro y las buenas maneras en canales escritos o telefónicos. El uso de expresiones insultantes, despectivas, intimidatorias o abusivas hacia nuestro personal técnico o legal habilitará a Auditorialegal a resolver de forma inmediata el contrato, suspendiendo el acceso a las plataformas y borrando los datos sin derecho a reembolso de los importes devengados.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                These General Terms and Conditions govern the provision of professional cybersecurity auditing, technical consulting, and computer forensics expertise offered by Joaquín Burgos Bayo (hereinafter, "Auditorialegal").
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.1. Purpose and Scope of Application</h2>
                <p className="leading-relaxed">
                  These conditions apply to all consultations, express diagnoses, OSINT pre-analyses, and professional services formally contracted through the website or any other communication channel enabled by Auditorialegal.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.2. Diagnostic Requests and Technical Limits</h2>
                <p className="leading-relaxed mb-4">
                  By requesting an "Express Diagnosis", the user provides informed and express consent for the execution of passive and semi-passive information gathering (OSINT) techniques on the domain or domains declared to be owned by them or for which they have explicit authorization.
                </p>
                <p className="leading-relaxed">
                  Auditorialegal undertakes not to perform any intrusive analysis, active port scanning with traffic alteration, or vulnerability exploitation (active DAST) in this preliminary diagnosis that could compromise the availability or integrity of the user's systems without the prior signature of a pentesting consulting contract (technical assignment sheet).
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.3. Professional Secrecy and Confidentiality</h2>
                <p className="leading-relaxed">
                  By virtue of the nature of computer forensics and judicial expert services, all information shared by the client, findings in ENS or NIS2 audits, and resulting reports are strictly protected under Professional Secrecy in accordance with Article 542.3 of the Spanish Organic Law of the Judiciary and ethical standards of technological forensic expert witnessing.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.4. Limitation of Liability</h2>
                <p className="leading-relaxed">
                  The results of preliminary technical reports or automated diagnoses are based on the state of the visible external infrastructure on the exact date of execution. Cybersecurity is a dynamic process; therefore, Auditorialegal assumes no liability for incidents, intrusions, or security failures occurring after the report is issued.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.5. Applicable Law, ADR, and Jurisdiction</h2>
                <p className="leading-relaxed mb-4">
                  Spanish legislation shall apply to any matters not provided for herein. To expedite and reduce the cost of dispute resolution, the parties agree to attempt to resolve any conflict in good faith out of court.
                </p>
                <p className="leading-relaxed mb-4">
                  For disputes brought by users with the legal status of consumers, they shall be submitted to the **Consumer Arbitration System (Royal Decree 231/2008)**. For corporate B2B disputes between companies or professionals, the parties agree to submit to the institutional arbitration of the **Las Palmas Chamber of Commerce Arbitration Court**.
                </p>
                <p className="leading-relaxed">
                  Should arbitration not take place or be declared void, both parties submit, with express waiver of any other jurisdiction, to the exclusive jurisdiction of the Courts of Arrecife, Lanzarote (Spain), except where procedural law mandates a protective consumer forum.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4.6. Mutual Respect and Decorum</h2>
                <p className="leading-relaxed">
                  Communications and professional interactions between Auditorialegal and the client shall be governed by the principles of good faith and must maintain decorum and good manners at all times on written or telephone channels. The use of insulting, derogatory, intimidating, or abusive language toward our technical or legal staff will entitle Auditorialegal to terminate the contract immediately, suspending access and wiping data with no right to a refund.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PoliticaVentasContent = ({ inModal = false, renderDesistimientoForm = null }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Scale className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Condiciones Comerciales' : 'Commercial Terms'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Condiciones de Compra y Contratación' : 'Terms of Purchase and Contracting'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Regulación de Comercio Electrónico y Contratación B2B' : 'Regulation of E-Commerce and B2B Contracting'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                Las presentes Condiciones de Compra y Contratación regulan la adquisición y suscripción online a las licencias de software SaaS y servicios tecnológicos comercializados a través del sitio web de <strong>Joaquín Burgos Bayo - Auditorialegal</strong>.
              </p>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.1. Identificación del Vendedor</h2>
                <p className="leading-relaxed">
                  De conformidad con la Ley 34/2002 (LSSI-CE), el prestador de servicios comerciales y titular del comercio online es <strong>Joaquín Burgos Bayo</strong> con NIF <strong>44224426A</strong> y domicilio en Puerto del Carmen, Lanzarote (España).
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.2. Precios e Impuestos</h2>
                <p className="leading-relaxed mb-3">
                  El producto estrella SaaS <strong>ADCA Temporal (Pericial Time Registry)</strong> se comercializa en régimen de suscripción anual, mediante una estructura de tarifa plana transparente por tramos de personal:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-sm mb-3">
                  <li><strong>Hasta 10 empleados:</strong> 99 € / año.</li>
                  <li><strong>De 11 a 50 empleados:</strong> 199 € / año.</li>
                  <li><strong>De 51 a 250 empleados:</strong> 399 € / año.</li>
                  <li><strong>Más de 250 empleados (Corporativo):</strong> 799 € / año.</li>
                </ul>
                <p className="leading-relaxed text-xs text-muted-foreground bg-muted p-3 rounded">
                  <strong>Impuestos:</strong> Todos los precios indicados no incluyen el Impuesto sobre el Valor Añadido (IVA) vigente en España del 21%, el cual se desglosará de manera explícita durante el proceso de checkout y en la factura correspondiente, excepto en los casos legalmente exentos.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.3. Métodos de Pago y Activación</h2>
                <p className="leading-relaxed">
                  Los pagos se realizan a través de transferencia bancaria directa o pasarela segura SSL operada mediante Stripe (soportando tarjetas Visa, Mastercard y Bizum). Una vez confirmado el abono, nuestro equipo técnico configurará su portal corporativo y le entregará las claves del administrador en un plazo máximo de <strong>24 horas laborables</strong>.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.4. Derecho de Desistimiento (14 días)</h2>
                <p className="leading-relaxed mb-3">
                  De conformidad con el artículo 102 del Real Decreto Legislativo 1/2007, de 16 de noviembre, por el que se aprueba el texto refundido de la Ley General para la Defensa de los Consumidores y Usuarios, <strong>usted dispone del derecho a desistir del presente contrato en un plazo de 14 días naturales</strong> sin necesidad de justificación alguna.
                </p>
                <p className="leading-relaxed mb-3">
                  El plazo de desistimiento expirará a los 14 días naturales del día de la celebración del contrato (contratación del software/servicio online).
                </p>
                <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 rounded space-y-3">
                  <p className="text-xs text-foreground leading-normal">
                    <strong>Procedimiento de Desistimiento Inmediato y Simétrico (Directiva UE 2023/2673):</strong> En cumplimiento riguroso de la normativa europea de protección al consumidor digital, Auditorialegal habilita un botón oficial e interactivo de desistimiento digital in-app. Puede formalizar su desistimiento de forma instantánea rellenando el formulario oficial que se encuentra a continuación.
                  </p>
                  {renderDesistimientoForm && (
                    <div className="pt-2">
                      {renderDesistimientoForm}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.5. Reembolsos y Excepciones</h2>
                <p className="leading-relaxed">
                  En caso de desistimiento válido, procederemos a reembolsarle todo pago recibido de su parte en un plazo máximo de 14 días naturales a partir de la fecha de recepción del formulario de desistimiento oficial. Utilizaremos el mismo medio de pago empleado para la transacción inicial. 
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.6. Facturación Electrónica Acreditada</h2>
                <p className="leading-relaxed">
                  De conformidad con el Real Decreto 1619/2012 y la Ley Crea y Crece, Auditorialegal emite todas sus facturas en formato digital con firma electrónica acreditada mediante certificado digital oficial de la Fábrica Nacional de Moneda y Timbre (FNMT), garantizando plenamente ante la Agencia Tributaria (AEAT) la autenticidad de su origen y la integridad inalterable de su contenido telemático.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.7. Política de Impagos, Suspensión y Borrado Seguro de Datos (SGSI/RGPD)</h2>
                <p className="leading-relaxed">
                  La falta de pago de cualquier renovación periódica o cuota anual de los planes SaaS (ADCA Temporal, CISO Hub, etc.) provocará la suspensión automática del acceso tras 7 días naturales desde el envío de la notificación de incidencia de cobro. Si transcurridos 15 días naturales desde el vencimiento no se ha regularizado el pago, se tramitará la baja definitiva del servicio. Por razones de seguridad del SGSI y estricto cumplimiento del RGPD, la baja definitiva conllevará el borrado seguro e irreversible de todas las bases de datos de presencia, logs de tiempo y archivos corporativos alojados, teniendo cualquier reactivación posterior el tratamiento técnico de una nueva alta.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                These Conditions of Purchase and Contracting govern the online acquisition and subscription to SaaS software licenses and technological services marketed through the website of <strong>Joaquín Burgos Bayo - Auditorialegal</strong>.
              </p>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.1. Seller's Identification</h2>
                <p className="leading-relaxed">
                  In compliance with Law 34/2002 (LSSI-CE), the provider of commercial services and owner of the online commerce is <strong>Joaquín Burgos Bayo</strong> with NIF <strong>44224426A</strong>, located in Puerto del Carmen, Lanzarote (Spain).
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.2. Prices and Taxes</h2>
                <p className="leading-relaxed mb-3">
                  Our flagship SaaS product <strong>ADCA Temporal (Pericial Time Registry)</strong> is sold as an annual subscription, based on a transparent flat-rate structure grouped by staff headcount:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-sm mb-3">
                  <li><strong>Up to 10 employees:</strong> €99 / year.</li>
                  <li><strong>11 to 50 employees:</strong> €199 / year.</li>
                  <li><strong>51 to 250 employees:</strong> €399 / year.</li>
                  <li><strong>More than 250 employees (Corporate):</strong> €799 / year.</li>
                </ul>
                <p className="leading-relaxed text-xs text-muted-foreground bg-muted p-3 rounded">
                  <strong>Taxes:</strong> All listed prices do not include the 21% Value Added Tax (VAT) currently applicable in Spain, which will be explicitly itemized during checkout and on the invoice, unless legally exempt.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.3. Payment Methods and Activation</h2>
                <p className="leading-relaxed">
                  Payments are made via direct bank transfer or a secure SSL gateway operated through Stripe (supporting Visa, Mastercard, and Bizum). Once payment is confirmed, our technical team will set up your corporate portal and deliver your admin credentials within a maximum of <strong>24 business hours</strong>.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.4. Right of Withdrawal (14 days)</h2>
                <p className="leading-relaxed mb-3">
                  In accordance with Article 102 of the Royal Legislative Decree 1/2007, of November 16, which approves the revised text of the General Law for the Defense of Consumers and Users, <strong>you have the right to withdraw from this contract within 14 calendar days</strong> without any justification.
                </p>
                <p className="leading-relaxed mb-3">
                  The withdrawal period will expire 14 calendar days from the day the contract is concluded (contracting the software/online service).
                </p>
                <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 rounded space-y-3">
                  <p className="text-xs text-foreground leading-normal">
                    <strong>Immediate and Symmetrical Withdrawal Procedure (EU Directive 2023/2673):</strong> In strict compliance with European digital consumer protection regulations, Auditorialegal provides an official interactive in-app digital withdrawal button. You can execute your withdrawal instantly by filling out the official form below.
                  </p>
                  {renderDesistimientoForm && (
                    <div className="pt-2">
                      {renderDesistimientoForm}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.5. Refunds and Exceptions</h2>
                <p className="leading-relaxed">
                  In the event of a valid withdrawal, we will refund all payments received from you within a maximum of 14 calendar days from the date we receive your official withdrawal form. We will use the same payment method you used for the initial transaction.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.6. Accredited Electronic Invoicing</h2>
                <p className="leading-relaxed">
                  In compliance with Spanish Royal Decree 1619/2012, Auditorialegal issues all invoices electronically in a digital format secured with an accredited digital signature from Spain's National Mint (FNMT). This fully guarantees to tax authorities (AEAT) the authenticity of origin and the unalterable integrity of the invoice content.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-primary mb-3">5.7. Non-Payment Policy, Suspension, and Secure Data Wiping</h2>
                <p className="leading-relaxed">
                  Failure to pay any periodic renewal or annual subscription fee for our SaaS plans will trigger automatic access suspension 7 calendar days after the first billing failure notice. If 15 calendar days pass without payment regularisation, the service will be permanently terminated. For strict compliance with GDPR and information security policies, permanent termination will trigger a secure, irreversible wipe of all corporate presence logs, time-tracking databases, and hosted files, treating any subsequent reactivation technically as a completely new account setup.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const DeclaracionAccesibilidadContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Scale className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Conformidad Legal' : 'Legal Compliance'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Declaración de Accesibilidad' : 'Accessibility Statement'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Cumplimiento del Real Decreto 1112/2018' : 'Compliance with Royal Decree 1112/2018'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                Joaquín Burgos Bayo (en adelante, "Auditorialegal") se ha comprometido a hacer accesible su sitio web de conformidad con el <strong>Real Decreto 1112/2018, de 7 de septiembre</strong>, sobre accesibilidad de los sitios web y aplicaciones para dispositivos móviles del sector público, que transpone la Directiva (UE) 2016/2102.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Situación de Conformidad</h2>
                <p className="leading-relaxed">
                  Este sitio web es <strong>parcialmente conforme</strong> con el RD 1112/2018 y las pautas de accesibilidad **WCAG 2.1 nivel AA** (norma UNE-EN 301549:2022), debido a la falta de conformidad de algunos aspectos técnicos que se detallan a continuación.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Contenido no Accesible</h2>
                <p className="leading-relaxed mb-3">El contenido que se detalla a continuación no es accesible por lo siguiente:</p>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li><strong>Falta de conformidad con el RD 1112/2018:</strong>
                    <ul className="ml-6 mt-1 list-circle list-inside space-y-1 text-slate-400">
                      <li>Podrían existir fallos puntuales de contraste en elementos interactivos del mapa u objetos gráficos específicos cargados por scripts externos.</li>
                      <li>Determinados documentos informativos periciales antiguos en formato PDF pueden no cumplir la totalidad de requisitos de accesibilidad documental.</li>
                      <li>Algunas etiquetas estructurales de maquetación en el menú móvil dinámico podrían carecer transitoriamente de descripciones de rol ARIA.</li>
                    </ul>
                  </li>
                  <li><strong>Carga desproporcionada:</strong> No aplica.</li>
                  <li><strong>Contenido fuera del ámbito de la legislación aplicable:</strong> Archivos ofimáticos en formatos PDF u otros publicados antes del 20 de septiembre de 2018 que no resulten estrictamente necesarios para el desarrollo de la actividad administrativa/consultora.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Preparación de la Presente Declaración</h2>
                <p className="leading-relaxed mb-3">
                  La presente declaración fue preparada el <strong>24 de mayo de 2026</strong>.
                </p>
                <p className="leading-relaxed">
                  El método empleado para la preparación de la declaración ha sido una <strong>autoevaluación pericial tecnológica</strong> llevada a cabo por Auditorialegal, complementada con herramientas automatizadas de auditoría de accesibilidad (Lighthouse de Google, hachas de contraste WCAG y verificadores W3C), garantizando una revisión continua y la corrección proactiva de errores detectados.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Buzón de Accesibilidad, Quejas y Sugerencias</h2>
                <p className="leading-relaxed mb-3">
                  Usted puede realizar comunicaciones sobre requisitos de accesibilidad (Art. 10.2.a del RD 1112/2018) tales como:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm mb-4">
                  <li>Informar sobre cualquier posible incumplimiento por parte de este sitio web.</li>
                  <li>Transmitir otras dificultades de acceso al contenido.</li>
                  <li>Formular cualquier otra consulta o sugerencia de mejora relativa a la accesibilidad del sitio web.</li>
                </ul>
                <p className="leading-relaxed">
                  Estas comunicaciones deben dirigirse al correo electrónico <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold">info@auditorialegal.es</a> o llamando al teléfono <strong>633 115 067</strong>. Las quejas y reclamaciones recibidas serán respondidas formalmente en un plazo máximo de <strong>15 días hábiles</strong>.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                <h3 className="text-sm font-bold text-primary mb-2">Procedimiento de Aplicación</h3>
                <p className="text-xs text-foreground leading-relaxed">
                  Si una vez realizada una solicitud de información accesible o queja, esta hubiera sido desestimada, o no se estuviera de acuerdo con la decisión adoptada, el interesado podrá iniciar una reclamación ante la Unidad de Accesibilidad de la Secretaría de Estado de Digitalización e Inteligencia Artificial (SEDIA) o el organismo de control competente de conformidad con el Art. 13 del RD 1112/2018.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                Joaquín Burgos Bayo (hereinafter, "Auditorialegal") is committed to making its website accessible in accordance with <strong>Royal Decree 1112/2018, of September 7</strong>, on the accessibility of websites and mobile applications of the public sector, which transposes Directive (EU) 2016/2102.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Compliance Status</h2>
                <p className="leading-relaxed">
                  This website is <strong>partially compliant</strong> with RD 1112/2018 and the **WCAG 2.1 level AA** accessibility guidelines (UNE-EN 301549:2022 standard), due to the lack of conformity of some technical aspects detailed below.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Non-Accessible Content</h2>
                <p className="leading-relaxed mb-3">The content listed below is not accessible for the following reasons:</p>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li><strong>Non-compliance with RD 1112/2018:</strong>
                    <ul className="ml-6 mt-1 list-circle list-inside space-y-1 text-slate-400">
                      <li>Potential specific contrast issues in interactive elements of the map or specific graphic objects loaded by external scripts.</li>
                      <li>Certain legacy forensic informative documents in PDF format might not fully meet all document accessibility requirements.</li>
                      <li>Some structural layout tags in the dynamic mobile menu may temporarily lack ARIA role descriptions.</li>
                    </ul>
                  </li>
                  <li><strong>Disproportionate burden:</strong> Not applicable.</li>
                  <li><strong>Content outside the scope of applicable legislation:</strong> Office documents in PDF or other formats published before September 20, 2018 that are not strictly necessary for the development of administrative/consultative activities.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Preparation of this Accessibility Statement</h2>
                <p className="leading-relaxed mb-3">
                  This statement was prepared on <strong>May 24, 2026</strong>.
                </p>
                <p className="leading-relaxed">
                  The methodology used to prepare this statement was a <strong>technological forensic self-assessment</strong> carried out by Auditorialegal, complemented with automated accessibility auditing tools (Google Lighthouse, WCAG contrast checkers, and W3C validators), ensuring continuous review and proactive correction of detected issues.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Accessibility Mailbox, Complaints, and Suggestions</h2>
                <p className="leading-relaxed mb-3">
                  You can submit communications regarding accessibility requirements (Art. 10.2.a of RD 1112/2018) such as:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm mb-4">
                  <li>Report any potential non-compliance on this website.</li>
                  <li>Relay other difficulties in accessing the content.</li>
                  <li>Submit any other query or suggestion for improvement regarding the accessibility of the website.</li>
                </ul>
                <p className="leading-relaxed">
                  These communications should be directed to the email address <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold">info@auditorialegal.es</a> or by calling <strong>+34 633 115 067</strong>. Received complaints and claims will be formally answered within a maximum of <strong>15 business days</strong>.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg">
                <h3 className="text-sm font-bold text-primary mb-2">Application Procedure</h3>
                <p className="text-xs text-foreground leading-relaxed">
                  If an informational request or complaint is dismissed, or you do not agree with the decision adopted, you may initiate a complaint with the Accessibility Unit of the State Secretariat for Digitization and Artificial Intelligence (SEDIA) or the competent regulatory body in accordance with Art. 13 of RD 1112/2018.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const CanalDenunciasContent = ({ inModal = false, renderDenunciaForm = null }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Protección de Datos e Informante' : 'Whistleblower & Data Protection'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Canal de Denuncias Seguro y Anónimo' : 'Secure and Anonymous Reporting Channel'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Sistema Interno de Información - Ley 2/2023' : 'Internal Information System - Law 2/2023'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2 mb-8'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento con la **Ley 2/2023, de 20 de febrero**, reguladora de la protección de las personas que informen sobre infracciones normativas y de lucha contra la corrupción, Auditorialegal pone a disposición su **Sistema Interno de Información**. Este canal es el medio preferente para comunicar presuntas infracciones del derecho de la Unión Europea e infracciones penales o administrativas graves o muy graves detectadas en el ámbito de nuestra actividad profesional.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Garantía Absoluta de Anonimato y Confidencialidad</h2>
                <p className="leading-relaxed">
                  Este canal garantiza la **máxima confidencialidad** de la identidad del informante y de cualquier tercero mencionado en la comunicación, así como de las actuaciones que se desarrollen en la gestión de la misma. De igual forma, el canal permite la **presentación de denuncias de forma totalmente anónima**, sin requerir datos identificativos. La información transmitida se procesa y cifra de extremo a extremo, impidiendo la trazabilidad de la dirección IP de origen.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Principio de Indemnidad y Prohibición de Represalias</h2>
                <p className="leading-relaxed">
                  De conformidad con el artículo 36 de la Ley 2/2023, Auditorialegal declara de forma expresa y solemne que **queda terminantemente prohibida cualquier clase de represalia** (incluyendo amenazas, discriminación o menoscabo profesional) contra las personas que presenten comunicaciones de buena fe haciendo uso de este canal. Las personas informantes tienen garantizado su derecho a la indemnidad y a la protección jurídica y de asesoramiento integral.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Plazos Legales de Tramitación</h2>
                <p className="leading-relaxed">
                  Las comunicaciones recibidas en este canal se gestionarán bajo el siguiente protocolo de tiempos:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-sm mt-3 ml-2">
                  <li><strong>Acuse de recibo:</strong> Envío de confirmación automática de recepción en un plazo máximo de **7 días naturales** (facilitando al informante el código de seguimiento).</li>
                  <li><strong>Plazo de investigación:</strong> El plazo máximo para dar respuesta a las actuaciones de investigación no superará los **3 meses** a partir del acuse de recibo, salvo casos de especial complejidad.</li>
                </ul>
              </div>

              <div className="bg-[#0b1329] border border-blue-500/10 p-5 rounded-lg">
                <h3 className="text-base font-bold text-primary mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-accent" />
                  Acceso al Buzón de Comunicaciones Seguro
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-4">
                  A continuación se habilita el formulario interactivo para registrar su comunicación. Al procesarse la denuncia, nuestro motor criptográfico generará un Hash de Trazabilidad digital (SHA-256) garantizando que la integridad del informe queda sellada para siempre sin almacenar metadatos invasivos.
                </p>
                {renderDenunciaForm}
              </div>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with **Law 2/2023, of February 20**, regulating the protection of persons who report regulatory breaches and fight against corruption, Auditorialegal makes its **Internal Information System** available. This channel is the preferred medium to communicate alleged breaches of European Union law and serious or very serious criminal or administrative infractions detected within our professional scope.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Absolute Guarantee of Anonymity and Confidentiality</h2>
                <p className="leading-relaxed">
                  This channel guarantees the **maximum confidentiality** of the whistleblower's identity and any third party mentioned, as well as all investigation steps. Likewise, the channel permits **totally anonymous reporting**, without requiring identification. Transmitted data is processed and encrypted end-to-end, blocking the traceability of the source IP address.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Protection Against Retaliation (Indemnity)</h2>
                <p className="leading-relaxed">
                  Pursuant to Article 36 of Law 2/2023, Auditorialegal solemnly declares that **any kind of retaliation is strictly prohibited** (including threats, discrimination, or professional detriment) against persons submitting reports in good faith. Whistleblowers are guaranteed their right to indemnity and comprehensive legal protection and advice.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Legal Processing Timelines</h2>
                <p className="leading-relaxed">
                  Reports received through this channel will be managed under the following response protocol:
                </p>
                <ul className="list-disc list-inside space-y-1.5 text-sm mt-3 ml-2">
                  <li><strong>Acknowledgement of receipt:</strong> Automatic delivery of receipt confirmation within a maximum of **7 calendar days** (providing the whistleblower with a tracking code).</li>
                  <li><strong>Investigation period:</strong> The maximum time to respond to investigation proceedings will not exceed **3 months** from the acknowledgement of receipt, except in cases of special complexity.</li>
                </ul>
              </div>

              <div className="bg-[#0b1329] border border-blue-500/10 p-5 rounded-lg">
                <h3 className="text-base font-bold text-primary mb-2 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-accent" />
                  Access to the Secure Reporting Box
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-4">
                  Below is the interactive form to submit your report. Once the whistleblower report is processed, our cryptographic engine will generate a unique digital Traceability Hash (SHA-256) ensuring the integrity of your report is sealed forever without storing invasive metadata.
                </p>
                {renderDenunciaForm}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const AvisoSeguridadContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Seguridad Corporativa' : 'Corporate Security'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Aviso de Seguridad' : 'Security Notice'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Prevención de Fraudes y Cumplimiento Ético' : 'Fraud Prevention & Ethical Compliance'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2 mb-8'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En Auditorialegal nos tomamos muy en serio la seguridad de la información. Siguiendo el marco de prevención del fraude tecnológico establecido por organismos nacionales e inspirado en las directrices de la Agencia Española de Protección de Datos (AEPD), publicamos este Aviso de Seguridad para alertar y proteger a nuestros clientes, colaboradores y usuarios frente a prácticas fraudulentas detectadas en el sector.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Alerta sobre Fraudes por Suplantación de Identidad (Phishing)</h2>
                <p className="leading-relaxed mb-3">
                  Se ha detectado un incremento de campañas fraudulentas que intentan suplantar la identidad de firmas consultoras de ciberseguridad mediante el envío de correos electrónicos maliciosos (phishing), mensajes instantáneos o llamadas de ingeniería social (vishing).
                </p>
                <p className="leading-relaxed">
                  <strong>Recuerde:</strong> Auditorialegal nunca le solicitará sus contraseñas, credenciales de acceso a servidores, códigos de autenticación doble factor (2FA) o datos bancarios completos por correo electrónico o llamada telefónica. Toda comunicación oficial se realiza exclusivamente desde el dominio <strong>@auditorialegal.es</strong>, provisto de firmas criptográficas SPF, DKIM y DMARC validadas. Si sospecha de cualquier comunicación sospechosa, absténgase de hacer clic en enlaces y notifíquelo de inmediato a <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-mono">info@auditorialegal.es</a>.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Advertencia sobre "Auditorías de Protección de Datos a Coste Cero"</h2>
                <p className="leading-relaxed mb-3">
                  En consonancia con las reiteradas advertencias públicas de la AEPD y de la Inspección de Trabajo, alertamos a las empresas sobre la ilegalidad de las ofertas de <strong>"auditorías de protección de datos a coste cero"</strong> o financiadas fraudulentamente a través de fondos de formación bonificada de la FUNDAE.
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-foreground">
                  <li>Estas prácticas, calificadas como **competencia desleal y fraude de subvenciones**, pueden acarrear sanciones de gravedad extrema tanto para las consultoras infractoras como para las empresas clientes que se deduzcan indebidamente dichos costes como formación.</li>
                  <li>Auditorialegal ejerce su actividad conforme a las más estrictas pautas deontológicas y de calidad técnica. Nuestros servicios profesionales se presupuestan de forma transparente y justa, garantizando la independencia del auditor y la excelencia en el peritaje tecnológico y cumplimiento del Esquema Nacional de Seguridad (ENS).</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Decálogo de Seguridad para el Cliente</h2>
                <p className="leading-relaxed mb-3">
                  Le recomendamos aplicar el siguiente decálogo de buenas prácticas al interactuar con nuestra plataforma o descargar informes:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm text-foreground">
                  <li>Verifique siempre que la URL del navegador sea <strong>https://www.auditorialegal.es</strong> y que la conexión esté cifrada bajo protocolo TLS (candado en la barra de direcciones).</li>
                  <li>No introduzca datos fiscales o información de su infraestructura organizativa en formularios web a menos que haya verificado los controles de la política de privacidad.</li>
                  <li>Compruebe la validez del sello de tiempo digital (TSA) y la firma electrónica de los informes periciales descargados remitiendo el documento a la plataforma gubernamental <strong>VALIDe</strong>.</li>
                </ol>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 24 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                At Auditorialegal we take information security very seriously. Following the framework for technological fraud prevention established by national authorities and inspired by the Spanish Data Protection Agency (AEPD) guidelines, we publish this Security Notice to alert and protect our clients, partners, and users against fraudulent practices detected in the sector.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Alert on Identity Theft Frauds (Phishing)</h2>
                <p className="leading-relaxed mb-3">
                  An increase in fraudulent campaigns trying to spoof the identity of cybersecurity consulting firms has been detected through malicious emails (phishing), instant messaging, or social engineering calls (vishing).
                </p>
                <p className="leading-relaxed">
                  <strong>Remember:</strong> Auditorialegal will never ask you for your passwords, server access credentials, two-factor authentication (2FA) codes, or full banking details via email or telephone. All official communication is made exclusively from the domain <strong>@auditorialegal.es</strong>, provided with validated SPF, DKIM, and DMARC cryptographic signatures. If you suspect any communication, refrain from clicking any links and report it immediately to <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-mono font-sans">info@auditorialegal.es</a>.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Warning on "Zero-Cost Data Protection Audits"</h2>
                <p className="leading-relaxed mb-3">
                  In line with repeated public warnings from the AEPD and the Labor Inspectorate, we alert businesses about the illegality of **"zero-cost data protection audits"** or audits fraudulently funded through FUNDAE subsidized training funds.
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-foreground">
                  <li>These practices, classified as **unfair competition and subsidy fraud**, can lead to extremely severe sanctions for both the infringing consulting firms and the client companies that improperly deduct these costs as training.</li>
                  <li>Auditorialegal operates in accordance with the strictest ethical and technical quality standards. Our professional services are priced transparently and fairly, guaranteeing auditor independence, technical expertise, and compliance with the National Security Scheme (ENS).</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Customer Security Rules</h2>
                <p className="leading-relaxed mb-3">
                  We recommend applying the following good practices when interacting with our platform or downloading reports:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm text-foreground">
                  <li>Always verify that the browser URL is <strong>https://www.auditorialegal.es</strong> and that the connection is encrypted under the TLS protocol (lock icon in the address bar).</li>
                  <li>Do not enter tax data or information about your organizational infrastructure on web forms unless you have verified the privacy policy controls.</li>
                  <li>Check the validity of the digital time stamp (TSA) and the electronic signature of downloaded expert reports by uploading the document to the government validation platform <strong>VALIDe</strong>.</li>
                </ol>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 24, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const RequisitosTecnicosContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Especificaciones Técnicas' : 'Technical Specifications'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Requisitos Técnicos y de Firma Electrónica' : 'Technical & Electronic Signature Requirements'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Plataforma Tecnológica y Verificación de Documentos' : 'Technological Platform and Document Verification'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2 mb-8'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                Para garantizar una navegación segura, accesible y plenamente compatible con las herramientas periciales y de cumplimiento integradas en este sitio web (React 18, PWA y Web Crypto API), se establecen las siguientes especificaciones y requisitos técnicos.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Requisitos de Navegación y Compatibilidad</h2>
                <p className="leading-relaxed mb-3">
                  La plataforma web de Auditorialegal está desarrollada bajo los estándares modernos de HTML5, CSS3 y JavaScript asíncrono, operando en modalidad de Aplicación Web Progresiva (PWA). Se recomienda utilizar las versiones más recientes de los siguientes navegadores web:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-foreground">
                  <li><strong>Google Chrome</strong> / <strong>Chromium</strong> (versión 100 o superior).</li>
                  <li><strong>Mozilla Firefox</strong> (versión 98 o superior).</li>
                  <li><strong>Microsoft Edge</strong> (versión 100 o superior).</li>
                  <li><strong>Apple Safari</strong> (versión 15 o superior).</li>
                </ul>
                <p className="leading-relaxed mt-3 text-xs text-muted-foreground">
                  Nota: El navegador debe tener habilitada la ejecución de JavaScript y soporte para cookies técnicas estrictamente necesarias para el correcto funcionamiento del ecosistema de seguridad, el Asistente Virtual IA y los analizadores criptográficos.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Estándares de Cifrado y Comunicaciones Seguras</h2>
                <p className="leading-relaxed mb-3">
                  Toda transferencia de información a través de esta plataforma (incluyendo formularios de diagnóstico express, consultas de chat e informes periciales) se realiza mediante un canal cifrado forzado bajo protocolo <strong>TLS 1.3 / TLS 1.2</strong> con algoritmos de intercambio de claves robustos.
                </p>
                <p className="leading-relaxed">
                  Nuestros servidores no admiten protocolos de cifrado obsoletos (como SSLv3, TLS 1.0 o TLS 1.1) para evitar ataques de degradación de seguridad (downgrade attacks), garantizando el cumplimiento de las guías de seguridad CCN-STIC del Centro Criptológico Nacional.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Verificación de Firmas Electrónicas e Informes Periciales</h2>
                <p className="leading-relaxed mb-3">
                  Todos los dictámenes periciales e informes de auditoría ENS descargables emitidos por Joaquín Burgos Bayo se firman electrónicamente con certificados reconocidos y cuentan con sellos de tiempo cualificados (TSA) que garantizan su autenticidad e integridad jurídica.
                </p>
                <p className="leading-relaxed mb-3">
                  Para verificar de manera externa e independiente la validez de los certificados e integridad de los ficheros PDF firmados por Auditorialegal, usted puede utilizar las plataformas oficiales de validación de firmas:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-foreground mb-4">
                  <li>
                    <strong>VALIDe:</strong> La plataforma gubernamental de Validación de Firmas y Certificados Online del Ministerio para la Transformación Digital y de la Función Pública. Acceso directo en:{' '}
                    <a
                      href="https://valide.redsara.es"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline break-all"
                    >
                      https://valide.redsara.es
                    </a>
                  </li>
                  <li>
                    Cualquier lector de PDF compatible con estándares de firma digital avanzada **PAdES** (como Adobe Acrobat Reader), que verificará de forma automática las firmas y el certificado de la Autoridad de Certificación emisora.
                  </li>
                </ul>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 24 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                To guarantee a secure, accessible, and fully compatible browsing experience with the expert and compliance tools integrated into this website (React 18, PWA, and Web Crypto API), the following technical specifications and requirements are established.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Browsing and Compatibility Requirements</h2>
                <p className="leading-relaxed mb-3">
                  Auditorialegal's web platform is developed under modern HTML5, CSS3, and asynchronous JavaScript standards, operating as a Progressive Web Application (PWA). We recommend using the latest versions of the following web browsers:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-foreground">
                  <li><strong>Google Chrome</strong> / <strong>Chromium</strong> (version 100 or higher).</li>
                  <li><strong>Mozilla Firefox</strong> (version 98 or higher).</li>
                  <li><strong>Microsoft Edge</strong> (version 100 or higher).</li>
                  <li><strong>Apple Safari</strong> (version 15 or higher).</li>
                </ul>
                <p className="leading-relaxed mt-3 text-xs text-muted-foreground">
                  Note: The browser must have JavaScript enabled and support strictly necessary technical cookies for the correct operation of the security ecosystem, the AI Virtual Assistant, and the cryptographic analyzers.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Encryption Standards and Secure Communications</h2>
                <p className="leading-relaxed mb-3">
                  Any transfer of information through this platform (including express diagnosis forms, chat queries, and expert reports) is performed through an encrypted channel forced under the **TLS 1.3 / TLS 1.2** protocol with robust key exchange algorithms.
                </p>
                <p className="leading-relaxed">
                  Our servers do not support legacy encryption protocols (such as SSLv3, TLS 1.0, or TLS 1.1) to prevent downgrade attacks, guaranteeing compliance with the CCN-STIC security guidelines of the National Cryptologic Center.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Verification of Electronic Signatures and Expert Reports</h2>
                <p className="leading-relaxed mb-3">
                  All downloadable expert witness reports and ENS audit reports issued by Joaquín Burgos Bayo are electronically signed with recognized certificates and contain qualified timestamps (TSA) that guarantee their authenticity and legal integrity.
                </p>
                <p className="leading-relaxed mb-3">
                  To verify the validity of certificates and the integrity of PDF files signed by Auditorialegal externally and independently, you can use the official signature validation platforms:
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-foreground mb-4">
                  <li>
                    <strong>VALIDe:</strong> The Spanish government's Online Signature and Certificate Validation platform of the Ministry for Digital Transformation and Public Function. Direct access at:{' '}
                    <a
                      href="https://valide.redsara.es"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:underline break-all"
                    >
                      https://valide.redsara.es
                    </a>
                  </li>
                  <li>
                    Any PDF reader compatible with **PAdES** advanced digital signature standards (such as Adobe Acrobat Reader), which will automatically verify the signatures and the certificate of the issuing Certification Authority.
                  </li>
                </ul>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 24, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const PoliticaCalidadContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Excelencia y Sostenibilidad' : 'Excelence & Sustainability'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Calidad Pericial y Sostenibilidad Digital' : 'Forensic Quality & Digital Sustainability Policy'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Compromiso con el Rigor Jurídico y la Transición Ecológica Digital' : 'Commitment to Legal Rigor and Digital Ecological Transition'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2 mb-8'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En <strong>Auditorialegal</strong>, bajo la dirección de Joaquín Burgos Bayo, asumimos un doble compromiso estratégico que nos sitúa a la vanguardia del sector tecnológico y del peritaje judicial: la búsqueda incansable de la <strong>máxima calidad probatoria y técnica</strong> en cada uno de nuestros dictámenes, combinada con un firme propósito de <strong>eco-responsabilidad digital y sostenibilidad green-tech</strong>.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Directrices de Calidad Pericial y Rigor Jurídico</h2>
                <p className="leading-relaxed mb-3">
                  Nuestros servicios de auditoría forense de ciberseguridad, certificación del Esquema Nacional de Seguridad (ENS) y peritajes judiciales están diseñados para aportar una fuerza probatoria incuestionable en sede judicial. Para ello, nos regimos por los siguientes estándares:
                </p>
                <ul className="space-y-3 list-disc list-inside text-sm text-foreground">
                  <li>
                    <strong>Validez Judicial Garantizada (Art. 335 LEC)</strong>: Cada informe y dictamen pericial es redactado bajo la estricta promesa de objetividad, veracidad y rigor pericial exigida por la Ley de Enjuiciamiento Civil española.
                  </li>
                  <li>
                    <strong>Cumplimiento de las Guías CCN-STIC</strong>: Aplicamos meticulosamente las metodologías de seguridad del Centro Criptológico Nacional (CCN-CNI) para la realización de auditorías de conformidad y análisis del estado de seguridad de los sistemas.
                  </li>
                  <li>
                    <strong>Custodia Electrónica Criptográfica</strong>: Toda evidencia digital recopilada durante los pentests o auditorías forenses es sellada en el momento de su obtención mediante firmas digitales avanzadas y funciones hash <strong>SHA-256</strong>, garantizando que el material no ha sufrido alteraciones desde su origen hasta su presentación en juicio.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Compromiso Medioambiental Digital: Sostenibilidad "Green-Tech"</h2>
                <p className="leading-relaxed mb-3">
                  Aunque nuestra actividad principal es inmaterial y se sitúa plenamente en el entorno del software y la consultoría jurídica digital, somos plenamente conscientes de que los servidores, bases de datos y ciclos de cómputo generan una huella ecológica real (huella de carbono digital). Por ello, nos comprometemos a mitigar y neutralizar nuestro impacto a través de las siguientes políticas:
                </p>
                <ul className="space-y-3 list-disc list-inside text-sm text-foreground">
                  <li>
                    <strong>Alojamiento "Green Cloud" y Servidores Eficientes</strong>: Todos los servicios e infraestructura en la nube operados directamente por Auditorialegal (incluyendo esta plataforma, herramientas SaaS como ADCA Temporal y bases de datos seguras) están alojados de forma exclusiva en centros de datos con certificación ecológica, un PUE (Power Usage Effectiveness) inferior a 1.25, y alimentados al 100% por fuentes de energía renovable.
                  </li>
                  <li>
                    <strong>Auditoría "Cero Papel" (Paperless Audit)</strong>: Hemos eliminado al 100% el uso de papel en nuestras operaciones. Las actas de auditoría, dictámenes periciales firmados, pre-diagnósticos, facturas e interacciones cliente-auditor se gestionan, archivan y firman exclusivamente en formato electrónico avanzado bajo estándares eIDAS.
                  </li>
                  <li>
                    <strong>Ciberseguridad Eco-Efficient</strong>: Diseñamos nuestros scripts de auditoría automatizada y herramientas de pentesting utilizando algoritmos optimizados que consumen la menor cantidad de ciclos de computación de CPU y transferencia de red posibles. Al reducir el consumo energético en la infraestructura de escaneo, disminuimos la huella de carbono digital directa asociada a la ejecución del software de seguridad.
                  </li>
                  <li>
                    <strong>Minimización de Residuos Electrónicos (E-Waste)</strong>: Promovemos el uso extendido del hardware profesional de análisis forense, adquiriendo equipos de alta durabilidad y procediendo a su reacondicionamiento y posterior reciclaje certificado en puntos autorizados una vez finalizada su vida útil.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Mejora Continua y Auditoría Interna</h2>
                <p className="leading-relaxed">
                  La ciberseguridad y la transición ecológica son procesos dinámicos en constante evolución. Auditorialegal se compromete a realizar revisiones periódicas anuales de esta Política de Calidad y Medioambiente Digital, evaluando nuevas oportunidades para reducir el consumo energético digital, optimizar la cadena de custodia probatoria e incorporar los últimos estándares técnicos oficiales dictados por el CCN, la AEPD y las directivas de la Unión Europea.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 25 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                At <strong>Auditorialegal</strong>, under the direction of Joaquín Burgos Bayo, we assume a double strategic commitment that positions us at the forefront of the technological and judicial expert sectors: the tireless pursuit of **maximum evidentiary and technical quality** in each of our reports, combined with a firm commitment to **digital eco-responsibility and green-tech sustainability**.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Forensic Quality Guidelines and Legal Rigor</h2>
                <p className="leading-relaxed mb-3">
                  Our cybersecurity forensic auditing, National Security Scheme (ENS) certification, and judicial expertise services are designed to provide unquestionable evidentiary strength in courts of law. To achieve this, we govern ourselves by the following standards:
                </p>
                <ul className="space-y-3 list-disc list-inside text-sm text-foreground">
                  <li>
                    <strong>Guaranteed Judicial Validity (Art. 335 LEC)</strong>: Each expert report is written under the strict promise of objectivity, truthfulness, and expert rigor required by Spanish Civil Procedure Law.
                  </li>
                  <li>
                    <strong>Compliance with CCN-STIC Guidelines</strong>: We meticulously apply the security methodologies of the National Cryptologic Center (CCN-CNI) for conducting compliance audits and analyzing system security states.
                  </li>
                  <li>
                    <strong>Cryptographic Electronic Custody</strong>: All digital evidence collected during pentests or forensic audits is sealed at the moment of capture using advanced digital signatures and **SHA-256** hash functions, guaranteeing the material has not been altered from its origin to its presentation in court.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Digital Environmental Commitment: "Green-Tech" Sustainability</h2>
                <p className="leading-relaxed mb-3">
                  Although our main activity is intangible and takes place fully in software and digital legal consulting, we are fully aware that servers, databases, and computing cycles generate a real ecological footprint (digital carbon footprint). Therefore, we commit to mitigating and neutralizing our impact through the following policies:
                </p>
                <ul className="space-y-3 list-disc list-inside text-sm text-foreground">
                  <li>
                    <strong>"Green Cloud" Hosting and Efficient Servers</strong>: All cloud infrastructure and services operated directly by Auditorialegal (including this platform, SaaS tools like ADCA Temporal, and secure databases) are hosted exclusively in eco-certified data centers, with a PUE (Power Usage Effectiveness) lower than 1.25, powered 100% by renewable energy.
                  </li>
                  <li>
                    <strong>Paperless Audit</strong>: We have eliminated paper use 100% in our operations. Audit reports, signed expert reports, pre-diagnoses, invoices, and client-auditor interactions are managed, archived, and signed exclusively in advanced electronic formats under eIDAS standards.
                  </li>
                  <li>
                    <strong>Eco-Efficient Cybersecurity</strong>: We design our automated audit scripts and pentesting tools using optimized algorithms that consume the minimal amount of CPU computing cycles and network bandwidth possible. By reducing energy consumption in scanning infrastructures, we decrease the direct digital carbon footprint associated with running security software.
                  </li>
                  <li>
                    <strong>Minimizing Electronic Waste (E-Waste)</strong>: We promote the extended use of professional forensic hardware, purchasing highly durable systems and proceeding with their refurbishment and certified recycling at authorized facilities once their lifespan ends.
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Continuous Improvement and Internal Auditing</h2>
                <p className="leading-relaxed">
                  Cybersecurity and digital green transitions are dynamic processes in constant evolution. Auditorialegal undertakes to conduct annual reviews of this Quality and Digital Environment Policy, evaluating new opportunities to reduce digital energy consumption, optimize the evidentiary chain of custody, and incorporate the latest official technical standards dictated by the CCN, AEPD, and European Union directives.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 25, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


export const PoliticaRedesSocialesContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2" />
            {lang === 'es' ? 'Protección de Datos en Redes' : 'Data Protection on Networks'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Privacidad en Redes Sociales' : 'Privacy Policy on Social Media Networks'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {lang === 'es' ? 'Ecosistema Social de Auditorialegal' : 'Social Ecosystem of Auditorialegal'}
          </p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2 mb-8'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground' : 'pt-8 space-y-8 text-foreground'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                De conformidad con lo establecido por el Reglamento (UE) 2016/679 (RGPD), la Ley Orgánica 3/2018 (LOPDGDD) y la Ley 34/2002 (LSSI-CE), <strong>Joaquín Burgos Bayo (en adelante, "Auditorialegal")</strong> informa a los usuarios de que ha procedido a crear perfiles oficiales en diversas redes sociales con la finalidad principal de publicitar y divulgar sus servicios y productos de ciberseguridad, auditoría forense del ENS y peritaje tecnológico judicial.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Datos Identificativos del Responsable</h2>
                <ul className="space-y-2 list-disc list-inside text-foreground">
                  <li><strong>Responsable del Tratamiento:</strong> Joaquín Burgos Bayo (Auditorialegal)</li>
                  <li><strong>NIF:</strong> 44224426A</li>
                  <li><strong>Domicilio Profesional:</strong> Puerto del Carmen, Edificio Atalaya Nº 17, C.P. 35510, Lanzarote.</li>
                  <li><strong>Correo Electrónico:</strong> info@auditorialegal.es</li>
                  <li><strong>Sitio Web Oficial:</strong> <a href="https://www.auditorialegal.es" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline font-mono">www.auditorialegal.es</a></li>
                </ul>
                <p className="leading-relaxed mt-4">
                  El usuario dispone de un perfil en la misma red social y ha decidido voluntariamente unirse o interactuar con la página o perfil oficial creado por <strong>Auditorialegal</strong>, mostrando así su consentimiento libre, inequívoco, específico e informado para el tratamiento de los datos personales publicados en su perfil en los términos descritos a continuación.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Consentimiento y Elaboración de Perfiles</h2>
                <p className="leading-relaxed mb-3">
                  Al interactuar con nuestros perfiles sociales (por ejemplo, dándole a "seguir", "me gusta", o compartiendo nuestros contenidos), usted nos facilita su consentimiento para el tratamiento de los datos personales que estén disponibles de forma pública en su respectivo perfil. 
                </p>
                <p className="leading-relaxed">
                  Asimismo, le informamos de que podríamos realizar análisis básicos y segmentaciones (elaboración de perfiles) a fin de conocer los servicios que más le puedan encajar (como auditorías ENS, ciberseguridad o peritaje forense) basándonos en sus preferencias de interacción o comentarios en las publicaciones de la firma. Los datos del usuario solo se tratan dentro del entorno de la propia red social y no se integran en las bases de datos persistentes de Auditorialegal sin mediar su previo consentimiento explícito.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Limitación de Edad y Menores</h2>
                <p className="leading-relaxed">
                  El acceso y registro en las principales redes sociales está prohibido para menores de **catorce (14) años**. Si el usuario es menor de catorce años, requiere obligatoriamente la asistencia, supervisión y consentimiento del titular de la patria potestad, tutor o representante legal para acceder e interactuar con la página oficial de Auditorialegal. Auditorialegal queda expresamente exonerada de cualquier responsabilidad derivada del uso de sus perfiles oficiales por menores de edad, correspondiendo dicha responsabilidad de forma íntegra a sus representantes legales.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Derechos del Interesado (RGPD y LOPDGDD)</h2>
                <p className="leading-relaxed mb-3">
                  Usted dispone del control total sobre su privacidad y sus datos. El RGPD le otorga los derechos de acceso, rectificación, limitación del tratamiento, supresión, portabilidad y oposición al tratamiento, así como a retirar el consentimiento en cualquier momento.
                </p>
                <p className="leading-relaxed">
                  Para ejercer estos derechos, puede dirigir su petición a Joaquín Burgos Bayo en el domicilio profesional en Lanzarote o al correo electrónico <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-mono">info@auditorialegal.es</a>. No obstante, le recordamos que dado que las redes sociales poseen su propia infraestructura, el ejercicio efectivo de estos derechos está limitado por la configuración técnica de la propia plataforma social. Le recomendamos dirigirse directamente a la entidad proveedora de la red social para ejercitar tales derechos sobre la información propia de su perfil de usuario.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">5. Utilización del Perfil Social por la Firma</h2>
                <p className="leading-relaxed mb-3">
                  En virtud de su condición de seguidor o usuario interactivo, Auditorialegal podrá llevar a cabo las siguientes acciones:
                </p>
                <ul className="space-y-2 list-disc list-inside text-sm text-foreground mb-3">
                  <li>Acceso a la información pública contenida en su perfil de usuario.</li>
                  <li>Publicación y difusión de noticias, posts, artículos y metodologías técnicas en los muros o líneas de tiempo correspondientes.</li>
                  <li>Envío de mensajes directos e individuales a través de los canales de chat de la respectiva red social.</li>
                  <li>Actualización periódica del estado del perfil oficial de la firma que se mostrará en su sección de noticias.</li>
                </ul>
                <p className="leading-relaxed">
                  El usuario posee plena libertad de revertir estas interacciones en cualquier momento mediante las opciones de privacidad de su propia red social (dejando de seguir la página, restringiendo contactos o eliminando sus comentarios).
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">6. Directrices de Publicación e Moderación</h2>
                <p className="leading-relaxed mb-3">
                  Una vez unido a nuestro perfil oficial, el usuario podrá interactuar publicando comentarios, enlaces, preguntas o material multimedia. En todos los casos, el usuario garantiza ser el titular exclusivo de los mismos, poseer los derechos de propiedad intelectual e industrial o contar con el consentimiento explícito de terceros implicados.
                </p>
                <p className="leading-relaxed mb-3">
                  <strong>Queda estrictamente prohibido</strong> publicar cualquier contenido (texto, imágenes o vídeos) que atente contra la legalidad, los derechos humanos, la moral, el decoro, el respeto o que vulnere la propiedad industrial o el derecho al honor y la imagen de terceros. 
                </p>
                <p className="leading-relaxed">
                  Auditorialegal se reserva el derecho de retirar de forma inmediata y unilateral cualquier publicación que contravenga estas normas, y de denunciar o bloquear de forma permanente al usuario infractor en la plataforma social. La firma declina toda responsabilidad sobre el contenido que libremente publique un usuario en nuestros perfiles oficiales.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">7. Concursos, Sorteos y Campañas</h2>
                <p className="leading-relaxed">
                  Auditorialegal se reserva el derecho a realizar promociones, sorteos informáticos o retos técnicos a través de sus redes. Las bases reguladoras de cada campaña se publicarán de forma transparente en los medios oficiales correspondientes de acuerdo con la LSSICE. Aclaramos que ninguna red social patrocina, avala ni administra en modo alguno nuestras promociones, ni está vinculada con ellas.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">8. Publicidad e Información de Producto</h2>
                <p className="leading-relaxed text-sm">
                  Nuestros canales sociales se utilizan de manera proactiva para divulgar los beneficios de ciberseguridad, dar a conocer parches de software o compartir novedades de nuestros productos (como la suite SaaS ADCA Temporal). Si decidiéramos realizar acciones comerciales directas con sus datos de contacto obtenidos de la red social, estas se ajustarán estrictamente al cumplimiento del RGPD, la LOPDGDD y la LSSICE. Recomendar o compartir las publicaciones de Auditorialegal no constituye una acción publicitaria comercial no solicitada.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">9. Enlaces a Políticas de Privacidad de Redes Sociales</h2>
                <p className="leading-relaxed mb-4">
                  Auditorialegal recomienda encarecidamente a todos los usuarios revisar periódicamente las políticas de privacidad y los ajustes de seguridad y cookies de las plataformas donde poseen cuenta. A continuación, facilitamos los accesos directos a las políticas de privacidad oficiales de las principales redes sociales:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <a 
                    href="https://www.facebook.com/help/323540651073243/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    Facebook: Política de Privacidad
                  </a>
                  <a 
                    href="https://twitter.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    X / Twitter: Centro de Privacidad
                  </a>
                  <a 
                    href="https://www.linkedin.com/legal/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    LinkedIn: Política de Privacidad
                  </a>
                  <a 
                    href="https://www.youtube.com/yt/policyandsafety/es/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    YouTube: Seguridad y Privacidad
                  </a>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200 sm:col-span-2 text-center"
                  >
                    Google: Centro de Privacidad Global
                  </a>
                </div>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In accordance with the provisions of Regulation (EU) 2016/679 (GDPR), the Organic Law 3/2018 (LOPDGDD), and Law 34/2002 (LSSI-CE), <strong>Joaquín Burgos Bayo (hereinafter, "Auditorialegal")</strong> informs users that it has created official profiles on several social networks with the main purpose of advertising and disseminating its cybersecurity, ENS forensic auditing, and technological expert services.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Data Controller Identification Details</h2>
                <ul className="space-y-2 list-disc list-inside text-foreground">
                  <li><strong>Data Controller:</strong> Joaquín Burgos Bayo (Auditorialegal)</li>
                  <li><strong>NIF:</strong> 44224426A</li>
                  <li><strong>Professional Address:</strong> Puerto del Carmen, Edificio Atalaya No. 17, C.P. 35510, Lanzarote, Spain.</li>
                  <li><strong>Email:</strong> info@auditorialegal.es</li>
                  <li><strong>Official Website:</strong> <a href="https://www.auditorialegal.es" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline font-mono font-sans">www.auditorialegal.es</a></li>
                </ul>
                <p className="leading-relaxed mt-4">
                  The user already has a profile on the same social network and has voluntarily decided to join or interact with the official profile created by <strong>Auditorialegal</strong>, thereby showing their free, unequivocal, specific, and informed consent for the processing of personal data published on their profile under the terms described below.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Consent and Profiling</h2>
                <p className="leading-relaxed mb-3">
                  By interacting with our social profiles (e.g., clicking "follow", "like", or sharing our content), you provide us with your consent for the processing of personal data available publicly on your respective profile.
                </p>
                <p className="leading-relaxed">
                  Furthermore, we inform you that we may conduct basic analysis and segmentation (profiling) to understand which of our services might fit you best (such as ENS audits, cybersecurity, or forensic expertise) based on your interaction preferences or comments on our firm's posts. User data is only processed within the social network environment and is not integrated into Auditorialegal's persistent databases without prior explicit consent.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Age Limitation and Minors</h2>
                <p className="leading-relaxed">
                  Access and registration on major social networks are prohibited for minors under **fourteen (14) years of age**. If the user is under fourteen, they strictly require the assistance, supervision, and consent of their parent, guardian, or legal representative to access and interact with Auditorialegal's official page. Auditorialegal is expressly exonerated from any liability arising from the use of its official profiles by minors, with such liability corresponding fully to their legal representatives.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Rights of the Data Subject (GDPR and LOPDGDD)</h2>
                <p className="leading-relaxed mb-3">
                  You have full control over your privacy and data. The GDPR grants you rights of access, rectification, restriction of processing, erasure, portability, and objection to processing, as well as the right to withdraw consent at any time.
                </p>
                <p className="leading-relaxed">
                  To exercise these rights, you may direct your request to Joaquín Burgos Bayo at the professional address in Lanzarote or via email to <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-mono font-sans">info@auditorialegal.es</a>. However, we remind you that since social networks have their own infrastructure, the effective exercise of these rights is limited by the technical configuration of the social platform itself. We recommend contacting the social network provider directly to exercise these rights regarding your profile details.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">5. Use of the Social Profile by the Firm</h2>
                <p className="leading-relaxed mb-3">
                  By virtue of your status as a follower or interactive user, Auditorialegal may perform the following actions:
                </p>
                <ul className="space-y-2 list-disc list-inside text-sm text-foreground mb-3">
                  <li>Access public information contained in your user profile.</li>
                  <li>Publish and disseminate news, posts, articles, and technical methodologies on the corresponding feeds or timelines.</li>
                  <li>Send direct individual messages through the chat channels of the respective social network.</li>
                  <li>Periodically update the status of the firm's official profile to be displayed on your news feed.</li>
                </ul>
                <p className="leading-relaxed">
                  The user has full freedom to revert these interactions at any time using the privacy options of their own social network (unfollowing the page, restricting contacts, or deleting their comments).
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">6. Guidelines for Publication and Moderation</h2>
                <p className="leading-relaxed mb-3">
                  Once joined to our official profile, the user can interact by publishing comments, links, questions, or multimedia material. In all cases, the user guarantees to be the exclusive owner of these materials, to hold the intellectual and industrial property rights, or to have obtained explicit consent from involved third parties.
                </p>
                <p className="leading-relaxed mb-3">
                  <strong>It is strictly prohibited</strong> to publish any content (text, images, or videos) that violates the law, human rights, morals, decency, or respect, or that infringes on third-party intellectual property or rights to honor and image.
                </p>
                <p className="leading-relaxed">
                  Auditorialegal reserves the right to immediately and unilaterally remove any publication that violates these rules, and to permanently report or block the infringing user on the social platform. The firm declines all responsibility for content freely published by users on our official profiles.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">7. Contests, Sweeps, and Campaigns</h2>
                <p className="leading-relaxed">
                  Auditorialegal reserves the right to run promotions, technical challenges, or sweepstakes through its social channels. The rules of each campaign will be published transparently in accordance with the LSSICE. We clarify that no social network sponsors, endorses, or administers our promotions in any way, nor is it linked with them.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">8. Advertising and Product Information</h2>
                <p className="leading-relaxed text-sm">
                  Our social channels are proactively used to share the benefits of cybersecurity, announce software patches, or share updates about our products (like the ADCA Temporal SaaS suite). If we decide to perform direct commercial actions with your contact details obtained from the social network, they will strictly comply with the GDPR, LOPDGDD, and LSSICE. Recommending or sharing Auditorialegal's posts does not constitute unsolicited commercial advertising.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">9. Links to Social Media Privacy Policies</h2>
                <p className="leading-relaxed mb-4">
                  Auditorialegal strongly recommends that all users periodically review the privacy policies and security/cookie settings of the platforms where they hold accounts. Direct links to the official privacy policies of major social networks are provided below:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <a 
                    href="https://www.facebook.com/help/323540651073243/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    Facebook: Privacy Policy
                  </a>
                  <a 
                    href="https://twitter.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    X / Twitter: Privacy Center
                  </a>
                  <a 
                    href="https://www.linkedin.com/legal/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    LinkedIn: Privacy Policy
                  </a>
                  <a 
                    href="https://www.youtube.com/yt/policyandsafety/es/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200"
                  >
                    YouTube: Safety & Privacy
                  </a>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="p-3 rounded-lg border border-slate-800 bg-slate-900/30 hover:bg-slate-900/60 hover:border-accent/40 transition-all font-medium text-slate-200 sm:col-span-2 text-center"
                  >
                    Google: Global Privacy Center
                  </a>
                </div>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {!inModal && (
        <Card className="border-accent/30 bg-accent-subtle">
          <CardContent className="pt-6 text-center">
            <Mail className="w-10 h-10 text-accent mx-auto mb-3" />
            <p className="text-sm text-foreground leading-relaxed">
              {lang === 'es' ? (
                <>¿Tienes dudas sobre cómo Auditorialegal gestiona tu privacidad en canales sociales? Escríbenos a <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold">info@auditorialegal.es</a></>
              ) : (
                <>Have doubts about how Auditorialegal manages your privacy on social channels? Write to us at <a href="mailto:info@auditorialegal.es" className="text-accent hover:underline font-semibold font-sans">info@auditorialegal.es</a></>
              )}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const ProtocoloAcosoLaboralContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <AlertTriangle className="w-4 h-4 mr-2 text-red-500" />
            {lang === 'es' ? 'Seguridad y Salud Laboral' : 'Occupational Health & Safety'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Protocolo de Prevención del Acoso Laboral y Sexual' : 'Protocol for the Prevention of Workplace & Sexual Harassment'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground text-left' : 'pt-8 space-y-8 text-foreground text-left'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento del artículo 48 de la Ley Orgánica 3/2007, de 22 de marzo, para la igualdad efectiva de mujeres y hombres, y del Real Decreto 901/2020, <strong>Auditorialegal</strong> establece el presente protocolo formal de actuación para prevenir, detectar y resolver situaciones de acoso laboral, sexual o por razón de sexo.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Declaración de Principios</h2>
                <p className="leading-relaxed">
                  Auditorialegal manifiesta su rotundo rechazo ante cualquier conducta de naturaleza laboral, sexual o de otra índole que atente contra la dignity, intimidad o integridad física y moral de los profesionales. Nos comprometemos a mantener un entorno de trabajo seguro, respetuoso y exento de conductas hostiles, humillantes u ofensivas, declarando una política de <strong>tolerancia cero</strong> ante el acoso en todas sus formas.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Ámbito de Aplicación</h2>
                <p className="leading-relaxed">
                  Este protocolo es de obligada observancia y aplica a todo el personal de Auditorialegal, independientemente de su categoría profesional, puesto de trabajo, tipo de contrato, ubicación física (presencial o teletrabajo) o relación mercantil. También extiende sus principios de protección a clientes, proveedores, colaboradores externos y subcontratistas que interactúen con la organización.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Definición de Conductas Prohibidas</h2>
                <div className="space-y-4 leading-relaxed font-light text-slate-300">
                  <p>Se consideran conductas estrictamente prohibidas y perseguibles en el marco del presente protocolo:</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Acoso Laboral (Mobbing):</strong> Comportamiento sistemático, hostil y prolongado en el tiempo dirigido a un trabajador, con la finalidad de degradar su entorno de trabajo, socavar su dignidad personal, infundir miedo o forzar su abandono del puesto.</li>
                    <li><strong>Acoso Sexual:</strong> Cualquier comportamiento, verbal o físico, de naturaleza sexual, realizado con el propósito o el efecto de atentar contra la dignidad de una persona, en particular cuando se crea un entorno intimidatorio, degradante u ofensivo.</li>
                    <li><strong>Acoso por Razón de Sexo:</strong> Cualquier comportamiento realizado en función del sexo o género de una persona, con el propósito o el efecto de atentar contra su dignidad y crear un entorno intimidatorio, degradante u ofensivo.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Procedimiento de Actuación y Resolución</h2>
                <p className="leading-relaxed mb-4 font-light text-slate-300">
                  El procedimiento está diseñado para ser ágil, sumamente discreto y proteger en todo momento la confidencialidad e intimidad de las partes implicadas. Se estructura en las siguientes fases:
                </p>
                <div className="space-y-4 font-light text-slate-300">
                  <div className="p-4 bg-slate-900/40 rounded-lg border border-slate-800">
                    <strong className="text-primary font-bold">Fase 1: Canalización y Denuncia Formal</strong>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      Cualquier persona que se considere víctima de acoso o que tenga conocimiento de una situación de acoso dentro de la firma puede presentar una denuncia formal de forma anónima o identificada a través del <strong>Canal de Denuncias Criptográfico</strong> integrado en la web. La denuncia se gestiona de forma encriptada localmente y se sella de manera pericial con firma temporal cualificada para salvaguardar su absoluta integridad jurídica.
                    </p>
                  </div>
                  <div className="p-4 bg-slate-900/40 rounded-lg border border-slate-800">
                    <strong className="text-primary font-bold">Fase 2: Apertura e Instrucción del Expediente</strong>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      Recibida la denuncia, se constituirá en un plazo máximo de 3 días hábiles una <strong>Comisión Instructora</strong> independiente. Se iniciará una fase de investigación y contraste de indicios, citando a las partes y a los posibles testigos a audiencias individuales totalmente reservadas, garantizando el derecho a la presunción de inocencia del denunciado.
                    </p>
                  </div>
                  <div className="p-4 bg-slate-900/40 rounded-lg border border-slate-800">
                    <strong className="text-primary font-bold">Fase 3: Informe de Conclusiones y Medidas Correctivas</strong>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      En un plazo máximo de 15 días hábiles desde la apertura del expediente, la Comisión Instructora emitirá un informe detallado con conclusiones técnicas y periciales. Si se constata la existencia de acoso, se propondrán medidas correctivas inmediatas, que pueden incluir la reorganización de funciones, la rescisión del contrato comercial o laboral, y la incoación del régimen disciplinario oficial.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">5. Garantías del Proceso</h2>
                <p className="leading-relaxed">
                  El proceso se rige bajo los principios estrictos de:
                </p>
                <ul className="space-y-2 list-disc list-inside text-foreground font-light leading-relaxed mt-2 text-slate-300">
                  <li><strong>Confidencialidad absoluta:</strong> Todo el expediente y las declaraciones son de acceso estrictamente restringido.</li>
                  <li><strong>Protección frente a represalias:</strong> Se prohíbe explícitamente cualquier acción perjudicial o represalia (directa o indirecta) contra la víctima, denunciantes o testigos colaboradores (conforme al Art. 36 de la Ley 2/2023).</li>
                  <li><strong>Tratamiento prioritario y urgente:</strong> Los plazos establecidos se computarán con carácter de urgencia.</li>
                </ul>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Nota Técnica:</strong> La presentación de denuncias sobre acoso a través de nuestro Canal de Denuncias Seguro genera una firma y un resumen criptográfico SHA-256 único, garantizando a la víctima la inalterabilidad forense de la fecha y hora de su reporte ante cualquier requerimiento judicial posterior.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with Article 48 of Organic Law 3/2007, of March 22, for the effective equality of women and men, and Royal Decree 901/2020, <strong>Auditorialegal</strong> establishes this formal protocol of action to prevent, detect, and resolve situations of workplace, sexual, or gender-based harassment.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Declaration of Principles</h2>
                <p className="leading-relaxed">
                  Auditorialegal expresses its absolute rejection of any conduct of a workplace, sexual, or other nature that violates the dignity, privacy, or physical and moral integrity of professionals. We commit to maintaining a safe, respectful work environment free of hostile, humiliating, or offensive conduct, declaring a <strong>zero tolerance</strong> policy toward harassment in all its forms.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Scope of Application</h2>
                <p className="leading-relaxed">
                  This protocol is mandatory and applies to all Auditorialegal staff, regardless of professional category, position, contract type, physical location (in-person or remote/telework), or commercial relationship. It also extends its protective principles to clients, suppliers, external partners, and subcontractors interacting with the organization.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Definition of Prohibited Conducts</h2>
                <div className="space-y-4 leading-relaxed font-light text-slate-300">
                  <p>The following conducts are strictly prohibited and prosecutable under this protocol:</p>
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>Workplace Harassment (Mobbing):</strong> Systematic, hostile, and prolonged behavior directed over time at a worker, with the purpose of degrading their work environment, undermining their personal dignity, instilling fear, or forcing them to leave their job.</li>
                    <li><strong>Sexual Harassment:</strong> Any behavior, verbal or physical, of a sexual nature, conducted with the purpose or effect of violating a person's dignity, particularly when creating an intimidating, degrading, or offensive environment.</li>
                    <li><strong>Gender-Based Harassment:</strong> Any behavior conducted based on a person's sex or gender, with the purpose or effect of violating their dignity and creating an intimidating, degrading, or offensive environment.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Process of Action and Resolution</h2>
                <p className="leading-relaxed mb-4 font-light text-slate-300">
                  The procedure is designed to be agile, highly discreet, and protect the confidentiality and privacy of the involved parties at all times. It is structured into the following phases:
                </p>
                <div className="space-y-4 font-light text-slate-300">
                  <div className="p-4 bg-slate-900/40 rounded-lg border border-slate-800">
                    <strong className="text-primary font-bold">Phase 1: Reporting & Formal Complaint</strong>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      Anyone who considers themselves a victim of harassment or has knowledge of harassment within the firm can submit a formal complaint anonymously or identified through the **Cryptographic Reporting Channel** integrated into the web. The report is managed locally under encryption and pericially sealed with a qualified timestamp to safeguard its absolute legal integrity.
                    </p>
                  </div>
                  <div className="p-4 bg-slate-900/40 rounded-lg border border-slate-800">
                    <strong className="text-primary font-bold">Phase 2: Investigation & Case Opening</strong>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      Upon receiving the complaint, an independent **Investigating Commission** will be constituted within a maximum of 3 business days. A phase of investigation and evidence gathering will begin, summoning the parties and potential witnesses to strictly confidential individual hearings, guaranteeing the respondent's right to presumption of innocence.
                    </p>
                  </div>
                  <div className="p-4 bg-slate-900/40 rounded-lg border border-slate-800">
                    <strong className="text-primary font-bold">Phase 3: Conclusions Report & Corrective Measures</strong>
                    <p className="mt-1 text-xs leading-relaxed text-slate-400">
                      Within a maximum of 15 business days from opening the case, the Investigating Commission will issue a detailed report with technical and expert conclusions. If harassment is verified, immediate corrective measures will be proposed, which may include reorganization of duties, termination of the commercial or labor contract, and initiation of official disciplinary proceedings.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">5. Guarantees of the Process</h2>
                <p className="leading-relaxed">
                  The process is strictly governed by the principles of:
                </p>
                <ul className="space-y-2 list-disc list-inside text-foreground font-light leading-relaxed mt-2 text-slate-300">
                  <li><strong>Absolute confidentiality:</strong> The entire file and testimonies are strictly restricted access.</li>
                  <li><strong>Protection against retaliation:</strong> Any adverse action or retaliation (direct or indirect) against the victim, whistleblowers, or collaborating witnesses is explicitly prohibited (in compliance with Art. 36 of Law 2/2023).</li>
                  <li><strong>Priority and urgent handling:</strong> Established deadlines will be calculated with maximum urgency.</li>
                </ul>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Technical Note:</strong> Submitting harassment reports through our Secure Whistleblowing Channel generates a unique SHA-256 cryptographic signature and digest, guaranteeing the victim the forensic unalterability of the report date and time before any subsequent judicial requirements.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PoliticaIAContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Cpu className="w-4 h-4 mr-2 text-purple-400" />
            {lang === 'es' ? 'Gobernanza Algorítmica' : 'Algorithmic Governance'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Gobernanza y Uso Ético de la Inteligencia Artificial (AI Act)' : 'Artificial Intelligence Governance & Ethical Use Policy (AI Act)'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground text-left' : 'pt-8 space-y-8 text-foreground text-left'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento del **Reglamento (UE) 2024/1689 del Parlamento Europeo y del Consejo (Ley de Inteligencia Artificial / AI Act)**, <strong>Auditorialegal</strong> establece su política marco de uso ético, supervisión técnica y gobernanza algorítmica de los sistemas de Inteligencia Artificial utilizados en este portal y en nuestras operaciones de consultoría legal-tech.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Declaración de Principios de IA Ética</h2>
                <p className="leading-relaxed">
                  Auditorialegal se compromete a que el uso de cualquier sistema automatizado de IA (incluyendo nuestro Asistente de IA Conversacional en el widget) se rija bajo los principios de **transparencia, no discriminación algorítmica, equidad, privacidad desde el diseño y control humano activo**. Ninguna decisión con efectos jurídicos o significativos sobre nuestros clientes o empleados será adoptada de forma automatizada por una IA sin supervisión pericial humana.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Clasificación de Riesgo del Sistema</h2>
                <p className="leading-relaxed">
                  Conforme al AI Act europeo, los sistemas integrados en nuestro portal (como el bot de atención informativa al cliente) están clasificados como **"Sistemas de Inteligencia Artificial de Bajo Riesgo"** o de propósito general:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-2 font-light">
                  <li><strong>Transparencia Informativa:</strong> Los usuarios son advertidos de forma inmediata y clara de que están interactuando con un agente conversacional basado en modelos de lenguaje (LLM).</li>
                  <li><strong>Identificación de Salidas:</strong> Todo texto o contenido generado por el sistema se etiqueta de forma transparente como salida sintética.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Supervisión Humana y Mitigación de Sesgos</h2>
                <p className="leading-relaxed">
                  Aunque nuestro asistente IA proporciona información ágil sobre tarifas, normativas y metodologías, Auditorialegal realiza auditorías técnicas periódicas para garantizar:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-2 font-light">
                  <li><strong>Mitigación activa de sesgos:</strong> Control de respuestas para evitar sesgos discriminatorios de género, raza o ideología.</li>
                  <li><strong>Exactitud y Veracidad:</strong> Supervisión de fuentes normativas para prevenir la "alucinación" de datos legales y mantener la exactitud de la información técnica facilitada.</li>
                  <li><strong>Supervisión Humana Activa:</strong> En cualquier momento, los usuarios pueden solicitar la instrucción o interlocución de un perito cualificado humano a través de nuestro correo corporativo.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Privacidad y Seguridad de los Datos (Zero-Retention)</h2>
                <p className="leading-relaxed font-light text-slate-600 dark:text-slate-300">
                  Nuestros modelos se ejecutan bajo entornos seguros. En sintonía con el RGPD y las directrices de ciberseguridad del ENS:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-2 font-light">
                  <li>Las conversaciones e interacciones introducidas en el chat **no se utilizan para entrenar modelos públicos de terceros**.</li>
                  <li>No se requiere la aportación de datos de carácter personal para interactuar con la IA de la web. Cualquier información de registro en el formulario de contacto se trata de forma totalmente separada bajo nuestra Política de Privacidad.</li>
                </ul>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Garantía del AI Act:</strong> Auditorialegal acredita que sus herramientas inteligentes respetan de manera estricta los derechos de propiedad intelectual de terceros y cumplen con la Carta de Derechos Fundamentales de la Unión Europea.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with **Regulation (EU) 2024/1689 of the European Parliament and of the Council (Artificial Intelligence Act / AI Act)**, <strong>Auditorialegal</strong> establishes its framework policy for ethical use, technical supervision, and algorithmic governance of the Artificial Intelligence systems used in this portal and in our legal-tech consulting operations.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Declaration of Ethical AI Principles</h2>
                <p className="leading-relaxed">
                  Auditorialegal commits that the use of any automated AI system (including our Conversational AI Assistant in the widget) will be governed by the principles of **transparency, algorithmic non-discrimination, fairness, privacy by design, and active human oversight**. No decision with legal or significant effects on our clients or employees will be adopted in a fully automated manner by an AI without human expert supervision.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Risk Classification of the System</h2>
                <p className="leading-relaxed">
                  In accordance with the European AI Act, the systems integrated into our portal (such as the customer informative customer chatbot) are classified as **"Low-Risk Artificial Intelligence Systems"** or general-purpose AI:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-2 font-light">
                  <li><strong>Informational Transparency:</strong> Users are immediately and clearly warned that they are interacting with a conversational agent based on large language models (LLMs).</li>
                  <li><strong>Output Identification:</strong> All text or content generated by the system is transparently labeled as synthetically generated output.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Human Oversight and Bias Mitigation</h2>
                <p className="leading-relaxed">
                  Although our AI assistant provides agile information on rates, regulations, and methodologies, Auditorialegal conducts periodic technical audits to guarantee:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-2 font-light">
                  <li><strong>Active bias mitigation:</strong> Checking responses to prevent discriminatory bias regarding gender, race, religion, or ideology.</li>
                  <li><strong>Accuracy and Veracity:</strong> Monitoring regulatory sources to prevent the "hallucination" of legal data and maintaining the precision of the technical information provided.</li>
                  <li><strong>Active Human Supervision:</strong> At any time, users may request the guidance or interaction of a human qualified expert through our corporate email.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">4. Data Privacy and Security (Zero-Retention)</h2>
                <p className="leading-relaxed font-light text-slate-600 dark:text-slate-300">
                  Our models run under secure environments. In line with the GDPR and ENS cybersecurity guidelines:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-2 font-light">
                  <li>Conversations and interactions entered in the chat **are not used to train public third-party models**.</li>
                  <li>No personal data is required to interact with the web AI. Any registration information in the contact form is treated fully separately under our Privacy Policy.</li>
                </ul>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">AI Act Guarantee:</strong> Auditorialegal certifies that its intelligent tools strictly respect the intellectual property rights of third parties and comply with the Charter of Fundamental Rights of the European Union.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const ProtocoloDesconexionDigitalContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Clock className="w-4 h-4 mr-2 text-amber-400" />
            {lang === 'es' ? 'Derecho del Trabajo' : 'Labor Law'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Protocolo de Desconexión Digital y Teletrabajo Seguro' : 'Digital Disconnection & Secure Teleworking Protocol'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground text-left' : 'pt-8 space-y-8 text-foreground text-left'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento del artículo 88 de la **Ley Orgánica 3/2018, de 5 de diciembre, de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD)** y del artículo 20.bis del Estatuto de los Trabajadores, <strong>Auditorialegal</strong> formaliza el presente protocolo que garantiza el derecho a la desconexión digital de sus profesionales fuera de su jornada laboral, así como las directrices para un teletrabajo seguro y confidencial.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Derecho a la Desconexión Digital</h2>
                <p className="leading-relaxed">
                  Auditorialegal reconoce el derecho de sus profesionales a no responder a ningún tipo de comunicación (correo electrónico, mensajes de mensajería instantánea, teléfono o notificaciones de la plataforma) una vez finalizada su jornada de trabajo. Se prohíbe el envío de instrucciones directas o requerimientos fuera de las horas de servicio establecidas, salvo en situaciones de fuerza mayor o urgencias operativas periciales extremas de ciberseguridad.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Directrices de Comunicación Ética</h2>
                <p className="leading-relaxed">
                  Para hacer efectivo este derecho, se establecen las siguientes buenas prácticas internas:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Programación de correos:</strong> Si un profesional redacta una comunicación fuera de la jornada del destinatario, deberá programar su envío para que se entregue dentro de las horas hábiles del siguiente día laborable.</li>
                  <li><strong>Respeto de descansos y vacaciones:</strong> Queda garantizada la inactividad digital total durante los períodos de descanso diario, semanal, vacaciones y bajas médicas o maternales/patresar.</li>
                  <li><strong>Reuniones eficientes:</strong> Se evitará la convocatoria de reuniones de trabajo virtuales en los 30 minutos previos a la finalización de la jornada laboral oficial.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Directrices de Teletrabajo Seguro y Confidencial</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  Con el fin de salvaguardar la privacidad de nuestros clientes y el riguroso cumplimiento de las guías del Esquema Nacional de Seguridad (ENS):
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Conexión Criptográfica:</strong> Todo teletrabajo o acceso remoto a sistemas de Auditorialegal deberá realizarse exclusivamente bajo canales VPN cifrados y autenticación multifactor (MFA).</li>
                  <li><strong>Confidencialidad en el hogar:</strong> Los profesionales deberán salvaguardar sus pantallas e informes físicos de la vista de terceros ajenos a la organización.</li>
                  <li><strong>Uso de dispositivos autorizados:</strong> Se prohíbe el uso de dispositivos personales (BYOD) no securizados y no homologados por el departamento técnico para gestionar datos de clientes.</li>
                </ul>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Garantía de Conciliación:</strong> Auditorialegal asume la desconexión digital como un elemento clave para la salud mental, la conciliación de la vida personal y laboral y la mejora de la productividad técnica y pericial.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with Article 88 of **Organic Law 3/2018, of December 5, on Personal Data Protection and Guarantee of Digital Rights (LOPDGDD)** and Article 20.bis of the Workers' Statute, <strong>Auditorialegal</strong> formalizes this protocol that guarantees the right to digital disconnection of its professionals outside their working hours, as well as the guidelines for secure and confidential teleworking.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Right to Digital Disconnection</h2>
                <p className="leading-relaxed">
                  Auditorialegal recognizes the right of its professionals not to respond to any type of communication (email, instant messaging, phone calls, or platform notifications) once their working day has ended. Sending direct instructions or requests outside the established service hours is prohibited, except in situations of force majeure or extreme forensic cybersecurity operational emergencies.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Ethical Communication Guidelines</h2>
                <p className="leading-relaxed">
                  To make this right effective, the following internal best practices are established:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Email scheduling:</strong> If a professional drafts a communication outside the recipient's working hours, they must schedule its delivery to arrive within the working hours of the next business day.</li>
                  <li><strong>Respect for rest periods and holidays:</strong> Total digital inactivity is guaranteed during daily and weekly rest periods, holidays, and medical, maternity, or paternity leaves.</li>
                  <li><strong>Efficient meetings:</strong> Convening virtual work meetings within 30 minutes prior to the end of the official working day will be avoided.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Secure & Confidential Teleworking Guidelines</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  In order to safeguard the privacy of our clients and strict compliance with the guidelines of the National Security Scheme (ENS):
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Cryptographic Connection:</strong> All teleworking or remote access to Auditorialegal systems must be carried out exclusively under encrypted VPN channels and multi-factor authentication (MFA).</li>
                  <li><strong>Confidentiality at home:</strong> Professionals must safeguard their screens and physical reports from the view of third parties outside the organization.</li>
                  <li><strong>Use of authorized devices:</strong> The use of unsecure and non-approved personal devices (BYOD) by the technical department to manage client data is prohibited.</li>
                </ul>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Work-Life Balance Guarantee:</strong> Auditorialegal embraces digital disconnection as a key element for mental health, work-life balance, and the enhancement of technical and forensic productivity.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PoliticaSeguridadInformacionContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <ShieldCheck className="w-4 h-4 mr-2 text-accent" />
            {lang === 'es' ? 'Esquema Nacional de Seguridad (ENS)' : 'National Security Scheme (ENS)'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Seguridad de la Información (PSI)' : 'Information Security Policy (ISP)'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground text-left' : 'pt-8 space-y-8 text-foreground text-left'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento del **Real Decreto 311/2022, de 3 de mayo, por el que se regula el Esquema Nacional de Seguridad (ENS)**, <strong>Auditorialegal</strong> aprueba y publica su Política de Seguridad de la Información corporativa. Esta política rige la protección de todos nuestros activos de información y sistemas de datos de clientes corporativos e institucionales.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Misión y Objetivos de Seguridad</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  La misión de Auditorialegal es proveer servicios de auditoría forense, consultoría legal-tech y peritajes de ciberseguridad con las máximas garantías de integridad y confidencialidad. Nuestros objetivos de seguridad de la información son:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li>Garantizar la confidencialidad de los datos de auditoría de nuestros clientes institucionales.</li>
                  <li>Mantener la absoluta disponibilidad y resiliencia técnica de nuestras herramientas (ADCA, Canal de Denuncias, SecureReceipt).</li>
                  <li>Asegurar la inalterabilidad forense e integridad de los informes y firmas criptográficas emitidas.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Marco Organizativo de Seguridad</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  La estructura organizativa y las responsabilidades periciales de seguridad están formalmente asignadas de la siguiente manera:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Responsable de Seguridad:</strong> Joaquín Burgos Bayo (Titular y Auditor Principal). Supervisa la implantación técnica de las directrices y las guías de ciberseguridad (CCN-STIC).</li>
                  <li><strong>Responsable del Sistema:</strong> Encargado de la infraestructura técnica y del correcto funcionamiento del cifrado de servidores y bases de datos seguras.</li>
                  <li><strong>Comité de Seguridad de la Información:</strong> Organismo colegiado que revisa anualmente la matriz de riesgos y actualiza las presentes directrices ante nuevas brechas o incidentes globales.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Gestión de Riesgos y Mejora Continua</h2>
                <p className="leading-relaxed">
                  Auditorialegal realiza un **Análisis de Riesgos formal anual** utilizando herramientas oficiales e indexando amenazas técnicas y físicas mediante la metodología **MAGERIT/PILAR**. Todo cambio sustancial en la red, en las bases de datos o en la arquitectura del software de los widgets requiere una evaluación de impacto previa y una validación pericial de la superficie de exposición técnica.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Garantía Pericial (Art. 335 LEC):</strong> El cumplimiento riguroso de nuestra PSI dota a todos nuestros informes y auditorías de una validez probatoria absoluta ante los tribunales de justicia españoles.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with **Royal Decree 311/2022, of May 3, which regulates the National Security Scheme (ENS)**, <strong>Auditorialegal</strong> approves and publishes its corporate Information Security Policy. This policy governs the protection of all our information assets and data systems of corporate and institutional clients.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Security Mission and Objectives</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  The mission of Auditorialegal is to provide forensic auditing, legal-tech consulting, and cybersecurity expertise services with the highest guarantees of integrity and confidentiality. Our information security objectives are:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li>Guarantee the confidentiality of the audit data of our institutional clients.</li>
                  <li>Maintain absolute availability and technical resilience of our tools (ADCA, Whistleblowing Channel, SecureReceipt).</li>
                  <li><strong>Information Security Committee:</strong> Joint body that annually reviews the risk matrix and updates these guidelines in response to new global breaches or incidents.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Risk Management and Continuous Improvement</h2>
                <p className="leading-relaxed">
                  Auditorialegal performs a formal **annual Risk Analysis** using official tools and indexing technical and physical threats through the **MAGERIT/PILAR** methodology. Any substantial change in the network, databases, or widget software architecture requires a prior impact assessment and a forensic validation of the technical exposure surface.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Expert Witness Guarantee (Art. 335 LEC):</strong> Strict compliance with our ISP provides all our reports and audits with absolute evidentiary validity before Spanish courts of law.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PoliticaIgualdadTratoContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Scale className="w-4 h-4 mr-2 text-accent" />
            {lang === 'es' ? 'Ley Integral 15/2022' : 'Comprehensive Law 15/2022'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Igualdad de Trato y No Discriminación' : 'Equality of Treatment and Non-Discrimination Policy'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground text-left' : 'pt-8 space-y-8 text-foreground text-left'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En cumplimiento de la **Ley 15/2022, de 12 de julio, integral para la igualdad de trato y la no discriminación (Ley Zerolo)**, <strong>Auditorialegal</strong> ratifica su compromiso ético y normativo de garantizar la plena igualdad de trato y evitar cualquier conducta que genere discriminación o exclusión en el ámbito laboral, mercantil y de atención al usuario.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Declaración de Compromiso Social y Ético</h2>
                <p className="leading-relaxed">
                  Auditorialegal asume como valor fundamental el derecho a la no discriminación de ninguna persona por motivos de nacimiento, origen racial o étnico, sexo, religión, convicción u opinión, edad, discapacidad, orientación o identidad sexual, expresión de género, enfermedad, estado socioeconómico o cualquier otra circunstancia personal o social.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Ámbito de Aplicación e Interacciones</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  Los principios de no discriminación y trato equitativo regulados en el presente protocolo aplican de manera estricta a:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Relaciones laborales e internas:</strong> Procesos de selección, contratación, formación y desarrollo profesional.</li>
                  <li><strong>Relaciones comerciales y de consumo:</strong> Prestación de servicios de consultoría legal-tech y atención a clientes corporativos e institucionales, garantizando igualdad de acceso.</li>
                  <li><strong>Desarrollo tecnológico:</strong> Cuidado estricto en el entrenamiento y control de sesgos algorítmicos de nuestras herramientas y modelos de Inteligencia Artificial.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Mecanismos de Mediación y Denuncia</h2>
                <p className="leading-relaxed">
                  Cualquier conducta contraria a esta política que sea detectada por profesionales, clientes o colaboradores de Auditorialegal puede ser denunciada inmediatamente de forma anónima o identificada a través de nuestro **Canal de Denuncias Seguro y Criptográfico**. La Comisión Instructora iniciará de inmediato un protocolo urgente de mediación y corrección para restablecer el derecho vulnerado y depurar responsabilidades disciplinarias o contractuales en menos de 7 días hábiles.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Cumplimiento Oficial:</strong> Esta declaración avala la plena adecuación ética mercantil de Auditorialegal de cara a pliegos e inscripciones oficiales ante las Administraciones Públicas y grandes corporaciones de la Unión Europea.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In compliance with **Law 15/2022, of July 12, comprehensive for equal treatment and non-discrimination (Zerolo Law)**, <strong>Auditorialegal</strong> ratifies its ethical and regulatory commitment to guarantee full equality of treatment and prevent any conduct that generates discrimination or exclusion in the workplace, commercial context, and user support.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Declaration of Social & Ethical Commitment</h2>
                <p className="leading-relaxed">
                  Auditorialegal assumes as a fundamental value the right to non-discrimination of any person on grounds of birth, racial or ethnic origin, sex, religion, conviction or opinion, age, disability, sexual orientation or identity, gender expression, illness, socio-economic status, or any other personal or social circumstance.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Scope of Application and Interactions</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  The principles of non-discrimination and equitable treatment regulated in this protocol strictly apply to:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Labor and internal relations:</strong> Selection, recruitment, training, and professional development processes.</li>
                  <li><strong>Commercial and consumer relations:</strong> Provision of legal-tech consulting services and assistance to corporate and institutional clients, guaranteeing equal access.</li>
                  <li><strong>Technological development:</strong> Strict care in the training and control of algorithmic biases of our Artificial Intelligence tools and models.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Mediation and Whistleblowing Mechanisms</h2>
                <p className="leading-relaxed">
                  Any conduct contrary to this policy detected by professionals, clients, or collaborators of Auditorialegal can be reported immediately, anonymously or with identification, through our **Secure & Cryptographic Whistleblowing Channel**. The Investigating Commission will immediately initiate an urgent protocol for mediation and correction to restore the infringed right and resolve disciplinary or contractual liabilities in less than 7 business days.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Official Compliance:</strong> This declaration certifies the full ethical and commercial alignment of Auditorialegal for tenders and official registrations before Public Administrations and large corporations in the European Union.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const PoliticaCadenaSuministroContent = ({ inModal = false }) => {
  const lang = useLang();
  return (
    <div className={inModal ? 'space-y-6' : 'max-w-4xl mx-auto'}>
      <TransparencyHeader />
      {!inModal && (
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            <Cpu className="w-4 h-4 mr-2 text-accent" />
            {lang === 'es' ? 'Directiva Europea NIS2' : 'European NIS2 Directive'}
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-primary mb-4">
            {lang === 'es' ? 'Política de Seguridad de la Cadena de Suministro (Vector 8)' : 'Supply Chain Security Policy (Vector 8)'}
          </h1>
          <p className="text-lg text-muted-foreground">www.auditorialegal.es</p>
        </div>
      )}

      <Card className={inModal ? 'border-0 shadow-none' : 'border-2'}>
        <CardContent className={inModal ? 'p-0 space-y-6 text-foreground text-left' : 'pt-8 space-y-8 text-foreground text-left'}>
          {lang === 'es' ? (
            <>
              <p className="leading-relaxed">
                En sintonía con las exigencias del **Artículo 21 de la Directiva (UE) 2022/2555 (NIS2)** y de la gobernanza contractual exigida en el **Vector 8** de seguridad corporativa, <strong>Auditorialegal</strong> detalla su política de homologación técnica, control de accesos y protección contractual aplicada sistemáticamente a todos sus subcontratistas y proveedores de servicios tecnológicos.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Principio de Minimización del Riesgo de Terceros</h2>
                <p className="leading-relaxed">
                  Auditorialegal reconoce que el 90% de los incidentes de ciberseguridad corporativos tienen su origen en fallos de configuración o brechas en sistemas de terceros. Asumimos el principio de **evaluación continua** de nuestra cadena de suministro para evitar la propagación indirecta de malware, brechas de datos o accesos indebidos en las redes de nuestros clientes.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Protocolo Técnico de Homologación de Proveedores</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  Todo proveedor tecnológico crítico (proveedores de infraestructura cloud, pasarelas de pago, bases de datos o gestores de scripts) que interactúe directa o indirectamente con los activos de información de la firma deberá superar los siguientes controles técnicos obligatorios:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Certificación de Seguridad:</strong> Presentación formal de certificaciones vigentes en el Esquema Nacional de Seguridad (ENS) o ISO 27001.</li>
                  <li><strong>Auditoría Técnica Externa (DAST/SAST):</strong> Verificación de la ausencia de vulnerabilidades críticas conocidas (OWASP Top 10) en sus interfaces y pasarelas expuestas.</li>
                  <li><strong>Garantía de Cifrado y Resiliencia:</strong> Uso estricto de cifrado TLS de grado militar (A+) y sistemas de backup resilientes a ataques de ransomware.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Gobernanza Contractual y Corresponsabilidad (Art. 28 RGPD)</h2>
                <p className="leading-relaxed">
                  Las relaciones con nuestros proveedores tecnológicos quedan estrictamente blindadas mediante **contratos de consultorías de encargo de tratamiento** (DPAs) firmados digitalmente. Estos contratos delimitan responsabilidades directas en ciberseguridad, regulan los acuerdos de nivel de servicio (SLAs), fijan la obligación de reportar incidentes técnicos en menos de 24 horas y autorizan de forma explícita la ejecución de auditorías forenses externas por parte de Auditorialegal.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">Compromiso NIS2:</strong> Esta metodología metodológica es aplicada de manera idéntica por Auditorialegal en sus consultorías de blindaje técnico para auditar y securizar de forma legal las cadenas de suministro de nuestros clientes de sector público y corporativo.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Última actualización: 26 de mayo de 2026
              </p>
            </>
          ) : (
            <>
              <p className="leading-relaxed">
                In line with the requirements of **Article 21 of Directive (EU) 2022/2555 (NIS2)** and the contractual governance required by **Vector 8** of corporate security, <strong>Auditorialegal</strong> details its policy of technical approval, access control, and contractual protection systematically applied to all its subcontractors and technological service providers.
              </p>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">1. Principle of Minimizing Third-Party Risk</h2>
                <p className="leading-relaxed">
                  Auditorialegal recognizes that 90% of corporate cybersecurity incidents originate from configuration failures or breaches in third-party systems. We adopt the principle of **continuous assessment** of our supply chain to prevent the indirect propagation of malware, data breaches, or unauthorized access into our clients' networks.
                </p>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">2. Technical Vendor Qualification Protocol</h2>
                <p className="leading-relaxed font-light text-slate-300">
                  Any critical technological provider (cloud infrastructure providers, payment gateways, databases, or script managers) interacting directly or indirectly with the firm's information assets must pass the following mandatory technical controls:
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-300 mt-2 font-light">
                  <li><strong>Security Certification:</strong> Formal presentation of active certifications under the National Security Scheme (ENS) or ISO 27001.</li>
                  <li><strong>External Technical Audit (DAST/SAST):</strong> Verification of the absence of known critical vulnerabilities (OWASP Top 10) in their interfaces and exposed gateways.</li>
                  <li><strong>Encryption & Resilience Guarantee:</strong> Strict use of military-grade TLS encryption (A+) and backup systems resilient to ransomware attacks.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">3. Contractual Governance and Joint Responsibility (Art. 28 GDPR)</h2>
                <p className="leading-relaxed">
                  Relationships with our technological providers are strictly secured by digitally signed **Data Processing Agreements (DPAs)**. These contracts delimit direct responsibilities in cybersecurity, regulate Service Level Agreements (SLAs), establish the obligation to report technical incidents in less than 24 hours, and explicitly authorize the execution of external forensic audits by Auditorialegal.
                </p>
              </div>

              <div className="bg-accent-subtle border-l-4 border-accent p-6 rounded-r-lg mt-6">
                <p className="text-sm text-foreground leading-relaxed">
                  <strong className="text-primary">NIS2 Commitment:</strong> This methodological approach is applied identically by Auditorialegal in its technical protection consultancies to legally audit and secure the supply chains of our public sector and corporate clients.
                </p>
              </div>

              <p className="text-right text-[10px] text-muted-foreground mt-4">
                Last update: May 26, 2026
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};



